import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Purpose: The {@link Copilot} React component — a floating chat panel
 *          host SPAs drop in to give users an in-app assistant. Owns the
 *          visual UI; delegates the actual conversation to a
 *          ConversationDriver (from @ada/copilot-core) that wraps the
 *          shared LlmClient agent loop.
 *
 * Implementation notes:
 *   - The component creates its own LlmClient and ConversationDriver
 *     once per `appId` / `auth` change. It does NOT take a pre-built
 *     LlmClient as a prop, deliberately: the host should not need to
 *     know about the LLM transport — only about authentication. This is
 *     the simplest API surface and matches how credits-admin's `api.ts`
 *     module instantiates its CreditsClient internally.
 *   - Conversation state is local React state. P0 has no rehydration
 *     across reloads; that lands in P3 with DDB-backed working memory.
 *   - One in-flight send at a time. The textarea and submit button are
 *     disabled while waiting for the model so users can't fan out
 *     concurrent turns (which would also break the loose history-replay
 *     model in the driver).
 *   - The chat panel is `position: fixed` and renders in-place (no
 *     React portal). React 19 + the host's StrictMode have no issue
 *     with this; the styles namespace prevents clashes with host CSS.
 *   - Voice (`voice="browser"`, the default) puts two labelled buttons in
 *     the composer whenever the browser supports Web Speech: **Dictate**
 *     (speech is typed into the text box; nothing is sent until the user
 *     presses Send) and **Talk** (hands-free voice conversation: listen →
 *     auto-send → read the reply aloud → listen again, until ended). The
 *     header keeps one speaker toggle ("read replies aloud" in text mode),
 *     default OFF and persisted per `appId` under `ada-copilot-voice:<appId>`.
 *     `voice="none"` is the developer kill switch — it hides every voice
 *     affordance regardless of the persisted state.
 *   - Focus: the text box is never `disabled` (a disabled element drops
 *     focus, which forced users to click back after every message). While
 *     a turn is in flight the user may keep typing; only sending is
 *     blocked. Focus returns to the text box after each reply, but only
 *     when focus was inside the widget (or nowhere) — never stolen from
 *     the host page.
 *   - UI strings come from `./i18n.ts` (built-in en + es, language from
 *     the `lang` prop → `<html lang>` → browser), overridable per string
 *     via the `strings` prop.
 *
 * Created: 2026-05-08 17:00
 *
 * Changelog:
 *   - 2026-05-08 17:00 — Initial version. P0 surface — text-only chat,
 *                        empty client-tool registry, no memory.
 *   - 2026-05-09 15:45 — P1: forward a `serverTools` prop to the driver
 *                        so the host can opt in to specific server-
 *                        resolved tools (e.g. docs_get, credits_*).
 *   - 2026-05-09 16:45 — P2: accept a `tools` prop (array of
 *                        `ClientToolDescriptor`s, typically built via
 *                        `defineTool`) and register them on the driver's
 *                        client-tool registry. Re-registers when the
 *                        array reference changes; hosts should `useMemo`
 *                        their tool list.
 *   - 2026-05-09 17:00 — P3: persist each turn server-side via
 *                        `copilot.conversations.save` and add a small
 *                        history view (button in the header, list panel
 *                        with new-conversation + load-by-id). Auto-title
 *                        derived from the first user turn; the user can
 *                        rename later via the (yet-unbuilt) UI affordance
 *                        — the JSON-RPC method already exists.
 *   - 2026-05-09 18:00 — P6: implement `voice="browser"`. Adds a mic
 *                        push-to-talk button next to the send button
 *                        (when supported) and TTS-speaks each assistant
 *                        reply. Hidden when the browser lacks Web Speech.
 *                        `voice="llm"` (vendor-side STT/TTS) is still a
 *                        future path — wiring stays in place.
 *   - 2026-05-09 19:00 — Slice 2 of P5 streaming: render assistant text
 *                        char-by-char via `driver.stream()`. A live
 *                        "streaming" bubble accumulates chunks; on
 *                        `tool-call` events a small status line shows
 *                        the active tool names; on `final` the bubble
 *                        is committed into `messages` and persistence
 *                        + TTS fire once with the aggregate text.
 *   - 2026-05-14 14:00 — Add a `layout` prop: 'popup' (default, current
 *                        floating-launcher behaviour), 'docked-left',
 *                        'docked-right' (always-visible side pane), and
 *                        'inline' (renders in normal flow, fills its
 *                        parent). Docked / inline modes hide the
 *                        launcher and the in-header close button — the
 *                        host owns visibility by mounting/unmounting.
 *   - 2026-05-15 17:00 — User-controlled voice toggles. Flip the default
 *                        for `voice` from `'none'` to `'browser'`: the
 *                        panel header now exposes a mic toggle and a
 *                        speaker toggle (both default OFF, persisted in
 *                        `localStorage` per appId). The composer mic
 *                        button shows only when the user enables the mic;
 *                        TTS-speak only fires when the user enables the
 *                        speaker. `voice="none"` stays as the developer
 *                        kill switch and hides every voice affordance.
 *   - 2026-05-15 18:30 — Add a `conversations` prop with three modes:
 *                        `'persistent'` (default, server-side via
 *                        `copilot.conversations.*` — unchanged behaviour),
 *                        `'local'` (browser `localStorage`, no server
 *                        round-trip), and `'disabled'` (no persistence,
 *                        history button hidden). All call sites go
 *                        through a small `ConversationsStorage` adapter
 *                        from `./conversations-storage.ts` so the React
 *                        code is mode-agnostic.
 *   - 2026-05-15 19:00 — Add a `voiceLang` prop (BCP-47 tag like
 *                        `'es-ES'`). When set, the same tag is forwarded
 *                        to both STT (`SpeechRecognition.lang`) and TTS
 *                        (`SpeechSynthesisUtterance.lang`), overriding
 *                        the browser default for both directions. Hosts
 *                        that need split input/output languages can be
 *                        served by a follow-up prop if it ever becomes
 *                        a real ask; one knob covers the common case.
 *   - 2026-05-16 10:30 — File-upload composer + optional model picker.
 *                        Adds three flags: `enableFileUpload` exposes a
 *                        paper-clip button and drag-and-drop on the
 *                        composer, gated on the active model's
 *                        `capabilities.fileInput`/`imageInput` (so a
 *                        text-only model hides the affordance entirely);
 *                        `enableModelPicker` surfaces a type-as-you-
 *                        filter combobox in the panel sourced from
 *                        `llm.model.list()`; `maxFileSizeBytes` caps
 *                        per-file size. Attachments ride inline as
 *                        `data:` URIs on each LLM turn — there is no
 *                        backend upload step — and are stripped before
 *                        `storage.save()` (a `[file: <name>]`
 *                        placeholder is prepended to `content`) so
 *                        reloaded conversations stay light. The model
 *                        list is fetched once on mount, cached for
 *                        both the picker and the capability gate.
 *   - 2026-05-16 13:00 — Add a `realtime` prop (CopilotRealtimeConfig)
 *                        that, when set, takes over rendering by
 *                        mounting `<VoiceWidget>` from
 *                        @ada/voice-widget. Mutually exclusive with the
 *                        sandwich `voice` prop — passing both throws.
 *                        Implementation is an early return at the top
 *                        of the component so none of the chat state
 *                        hooks fire when realtime is active; the two
 *                        transports stay structurally independent.
 *   - 2026-05-18 12:00 — Docked-mode collapse + drag-to-resize. The
 *                        `docked-left` / `docked-right` panels gain a
 *                        chevron in the header that collapses the panel
 *                        to a thin rail (with an expand chevron) and a
 *                        col-resize handle on the panel's free edge.
 *                        Both gestures persist per `appId` in
 *                        `localStorage` (key `ada-copilot-dock:<appId>`)
 *                        and are mirrored to host code as (a) the CSS
 *                        custom property `--ada-copilot-dock-width` on
 *                        `<html>` and (b) a new `onLayoutChange` prop
 *                        that fires on collapse-toggle and pointerup
 *                        (never per-move, so it is safe to set state
 *                        in). Width is clamped to [280 px,
 *                        min(50 vw, 800 px)] at runtime — a stored
 *                        width from a wider screen is re-clamped on
 *                        hydration. The previous CSS-only default
 *                        (`min(22rem, 100vw)`) still wins for hosts
 *                        that never touch the rail or handle, so
 *                        existing pages render byte-identically.
 *   - 2026-05-18 21:35 — Expand `CopilotRealtimeConfig` to mirror every
 *                        option `llm.realtime.session.create` accepts:
 *                        audioInput / audioOutput / toolChoice /
 *                        outputModalities / temperature /
 *                        maxOutputTokens / tracing / prompt / include /
 *                        sessionOverrides / expiresInSeconds. Legacy
 *                        `voice` / `maxResponseOutputTokens` /
 *                        `maxSessionSeconds` stay accepted and forward.
 *   - 2026-06-01 12:00 — Persist realtime (voice) sessions into the shared
 *                        conversation store. Extracted `<RealtimeCopilot>`
 *                        which subscribes to `VoiceWidget.onTranscript`,
 *                        assembles one `CopilotMessage` per finalized turn,
 *                        and saves via the same storage backend the chat
 *                        path uses (same `{tenantId,userId,appId}` scope,
 *                        tagged `metadata.source='voice'`) so voice
 *                        conversations are recallable in chat mode. Also
 *                        default-enables user-side transcription so user
 *                        turns are captured. New conversation per session.
 *   - 2026-06-01 12:30 — Docked layouts now show a Close (✕) button in the
 *                        header alongside the collapse chevron. Closing
 *                        fully hides the panel and rail and reveals the
 *                        floating launcher (shared with popup mode) to
 *                        reopen it; the closed state is session-only and
 *                        not persisted, so a reload restores the open
 *                        docked panel. Why: docked users previously had no
 *                        way to dismiss the pane (only collapse to a rail).
 *   - 2026-06-01 14:10 — Collapsed docked rail is now a single full-height
 *                        <button> (was a div wrapping a small chevron
 *                        button), so a click anywhere on the strip reopens
 *                        the panel. Pairs with the rail z-index fix in
 *                        styles.ts: when a host's fixed header overlaps the
 *                        rail's top, the user can still click lower down to
 *                        reopen.
 *   - 2026-06-01 15:00 — Popup mode is now resizable from its top-left
 *                        corner (bottom-right stays anchored). Width/height
 *                        are clamped to the viewport (so it can grow to fill
 *                        the window but never spill off-screen) and persisted
 *                        per appId. On mount the stored size is re-clamped to
 *                        the current viewport, and a window-resize listener
 *                        re-fits a custom size if the window shrinks.
 *   - 2026-06-01 15:30 — Promote the popup to a full floating window: drag
 *                        the header to move it, drag any of the 8 edges/
 *                        corners to resize. The persisted geometry is now a
 *                        full rect {left,top,width,height} (was size-only),
 *                        re-clamped to fit the viewport — size AND position —
 *                        on load and on window resize. Also hide the floating
 *                        launcher while the popup is open (the header ✕ is the
 *                        closer); it only shows as the opener when closed.
 *   - 2026-06-03 08:00 — Render assistant markdown. Assistant turns and the
 *                        streaming buffer now go through `renderMarkdown`
 *                        (marked + DOMPurify) into a `.ada-copilot-md`
 *                        container instead of a raw text node, so bold /
 *                        lists / code / links render instead of showing
 *                        literal `**`, `-`, backtick syntax. User turns stay
 *                        raw text.
 *   - 2026-09-25 08:06 — UX overhaul from the Celestina embed feedback:
 *                        (1) focus stays in / returns to the text box
 *                        (textarea no longer disabled while pending);
 *                        (2) i18n — new `lang` + `strings` props, built-in
 *                        en/es, localized relative times, voice language
 *                        defaults to the UI language; (3) voice rework —
 *                        header mic toggle removed, composer gets Dictate
 *                        (fills the box, never auto-sends) and Talk
 *                        (hands-free loop) buttons, replies read aloud
 *                        as plain text; (4) Stop button (aborts the
 *                        in-flight turn, keeps any partial reply);
 *                        (5) `suggestions` prop (example-question chips),
 *                        typing dots, plain-language tool status from
 *                        `ClientToolDescriptor.label`, auto-growing text
 *                        box, IME-safe Enter, Enter hint, stick-to-bottom
 *                        scrolling; (6) `header="compact"`, full-screen
 *                        popup on phones, `part` attributes for ::part()
 *                        styling, `role="log"` message list.
 *   - 2026-09-25 08:27 — Review fixes: saves are serialized per conversation
 *                        "epoch" and run after the composer is released (a
 *                        Stop during the save could drop the new id and
 *                        duplicate the conversation; Talk mode could lose an
 *                        utterance); reset the read-aloud watermark on new /
 *                        loaded conversations (Talk mode stalled); abandon an
 *                        in-flight turn when loading a conversation; never
 *                        drop a Talk-mode transcript; matchMedia fallback for
 *                        Safari < 14.
 *   - 2026-10-06 17:20 — Opt-in `voice="realtime"`: the chat panel stays and
 *                        **Talk** runs an OpenAI Realtime session (WebRTC)
 *                        instead of Web Speech, via `useRealtimeTalk`. Spoken
 *                        turns land in the chat (and its saved history), the
 *                        user can interrupt by talking or with a new
 *                        Interrupt button, typed messages go into the voice
 *                        session while it is open, and `realtime` + `voice=
 *                        "realtime"` tunes the session instead of swapping
 *                        the panel. Backward compatible: `voice` still
 *                        defaults to `'browser'`, and `realtime` without
 *                        `voice="realtime"` still renders the voice-only
 *                        `<VoiceWidget>` panel.
 */
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { CopilotClient } from '@ada/copilot-client';
import { ClientToolRegistry, ConversationDriver, assembleCopilotSystemPrompt, } from '@ada/copilot-core';
import { LlmClient, } from '@ada/llm-client';
import { VoiceWidget } from '@ada/voice-widget';
import { markdownToPlainText, renderMarkdown } from './markdown.js';
import { createDisabledStorage, createLocalStorage, createServerStorage, } from './conversations-storage.js';
import { buildStrings, formatMessageCount, formatRelativeTime, formatString, resolveLanguage, } from './i18n.js';
import { useRealtimeTalk, } from './realtime-talk.js';
import { ensureStylesInjected } from './styles.js';
import { speak, speechSynthesisSupported, stopSpeaking, useSpeechToText, } from './voice.js';
/** Viewport width (px) below which the popup switches to full screen. */
const COMPACT_VIEWPORT_MAX_PX = 640;
/** Width of the collapsed rail. Matches the CSS rule `.ada-copilot-rail`. */
const RAIL_WIDTH_PX = 36;
/** Default expanded width when the user has not chosen one yet. Matches `min(22rem, 100vw)` modulo viewport clamping handled at runtime. */
const DEFAULT_DOCK_WIDTH_PX = 352; // 22rem at 16px root
/** Minimum sensible panel width before the chat chrome breaks. */
const MIN_DOCK_WIDTH_PX = 280;
/** Max as a fraction of the viewport so the panel never eats more than half the page. */
const MAX_DOCK_WIDTH_FRACTION = 0.5;
/** Absolute cap independent of viewport size. */
const MAX_DOCK_WIDTH_PX = 800;
function dockStorageKey(appId) {
    return `ada-copilot-dock:${appId}`;
}
function readPersistedDockState(appId) {
    if (typeof localStorage === 'undefined')
        return null;
    try {
        const raw = localStorage.getItem(dockStorageKey(appId));
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed.collapsed !== 'boolean' ||
            typeof parsed.widthPx !== 'number' ||
            !Number.isFinite(parsed.widthPx)) {
            return null;
        }
        return { collapsed: parsed.collapsed, widthPx: parsed.widthPx };
    }
    catch {
        return null;
    }
}
function writePersistedDockState(appId, state) {
    if (typeof localStorage === 'undefined')
        return;
    try {
        localStorage.setItem(dockStorageKey(appId), JSON.stringify(state));
    }
    catch {
        // Quota exceeded or storage disabled — silently ignore; the widget
        // still works, just without cross-reload memory of the user's
        // chosen geometry.
    }
}
function clampDockWidth(px, viewportWidth) {
    const viewportCap = Math.floor(viewportWidth * MAX_DOCK_WIDTH_FRACTION);
    const max = Math.max(MIN_DOCK_WIDTH_PX, Math.min(MAX_DOCK_WIDTH_PX, viewportCap));
    return Math.max(MIN_DOCK_WIDTH_PX, Math.min(max, px));
}
// ── Popup-mode floating-window geometry ───────────────────────────────
// In popup mode the panel is a free-floating window: drag the header to
// move it, drag any edge/corner to resize. Its full rect (left/top/width/
// height, viewport pixels) is persisted per appId. `null` rect means the
// panel hasn't been moved/resized yet and renders at the CSS default
// (anchored bottom-right) — the first gesture promotes it to an explicit
// rect captured from the DOM.
/** Gap kept between the window and every viewport edge when clamping. */
const POPUP_VIEWPORT_GAP_PX = 12;
/** Defaults mirror the CSS `min(22rem, …)` / `min(32rem, …)`. */
const DEFAULT_POPUP_WIDTH_PX = 352; // 22rem
const DEFAULT_POPUP_HEIGHT_PX = 512; // 32rem
const MIN_POPUP_WIDTH_PX = 280;
const MIN_POPUP_HEIGHT_PX = 360;
function popupStorageKey(appId) {
    return `ada-copilot-popup:${appId}`;
}
function readPersistedPopupRect(appId) {
    if (typeof localStorage === 'undefined')
        return null;
    try {
        const raw = localStorage.getItem(popupStorageKey(appId));
        if (!raw)
            return null;
        const p = JSON.parse(raw);
        const ok = (n) => typeof n === 'number' && Number.isFinite(n);
        if (!ok(p.left) || !ok(p.top) || !ok(p.width) || !ok(p.height))
            return null;
        return { left: p.left, top: p.top, width: p.width, height: p.height };
    }
    catch {
        return null;
    }
}
function writePersistedPopupRect(appId, rect) {
    if (typeof localStorage === 'undefined')
        return;
    try {
        localStorage.setItem(popupStorageKey(appId), JSON.stringify(rect));
    }
    catch {
        // Quota exceeded or storage disabled — ignore; geometry just won't
        // survive a reload.
    }
}
/**
 * Clamp a popup rect so it never drops below the usable minimum size and
 * always sits fully inside the viewport (size first, then position). This
 * is what enforces "fit to the viewport" when a rect stored on one screen
 * is restored on a smaller one.
 */
function clampPopupRect(rect, vw, vh) {
    const gap = POPUP_VIEWPORT_GAP_PX;
    const maxWidth = Math.max(MIN_POPUP_WIDTH_PX, vw - 2 * gap);
    const maxHeight = Math.max(MIN_POPUP_HEIGHT_PX, vh - 2 * gap);
    const width = Math.max(MIN_POPUP_WIDTH_PX, Math.min(maxWidth, rect.width));
    const height = Math.max(MIN_POPUP_HEIGHT_PX, Math.min(maxHeight, rect.height));
    // Keep the whole window on-screen; if it's wider/taller than the viewport
    // (tiny screen), pin to the top-left gap rather than going negative.
    const left = Math.max(gap, Math.min(rect.left, vw - gap - width));
    const top = Math.max(gap, Math.min(rect.top, vh - gap - height));
    return { left, top, width, height };
}
/**
 * Default OpenAI realtime speech-to-text model used to transcribe the
 * *user* side of a voice session. OpenAI only returns user transcripts
 * when `audio.input.transcription` is configured; without it we would
 * persist assistant turns but silently drop every user turn. This value
 * is forwarded verbatim to OpenAI, so it must be a model id OpenAI's
 * realtime API accepts (not an ada catalog alias).
 */
const DEFAULT_REALTIME_TRANSCRIPTION_MODEL = 'gpt-4o-mini-transcribe';
/**
 * Realtime (bidirectional voice) variant of `<Copilot>`. Renders the
 * WebRTC `<VoiceWidget>` transport and, on top of it, persists the live
 * transcript into the *same* conversation store the chat path uses — so
 * a spoken session lands in the same `{ tenantId, userId, appId }`
 * bucket and can be reopened/continued in chat mode (voice → chat
 * recall). Scope per the agreed design: one new conversation per voice
 * session; recall is one-directional (we do not seed a realtime session
 * from prior chat history).
 *
 * Extracted from `Copilot` so its conversation-store hooks live in their
 * own component and don't sit behind `Copilot`'s early `return` for the
 * realtime branch (which would break the Rules of Hooks).
 */
function RealtimeCopilot(props) {
    // The caller (`Copilot`) only renders this when `realtime` is set.
    const rt = props.realtime;
    const { appId, auth, apiUrl = '/api', conversations: conversationsMode = 'persistent', } = props;
    // Reuse the chat path's storage backends verbatim, driven by the same
    // `conversations` mode prop. `persistent` saves server-side via
    // `copilot.conversations.save` (the shared store); `local` keeps a
    // per-browser copy; `disabled` skips persistence entirely.
    const copilotClient = useMemo(() => new CopilotClient({ url: apiUrl, ...(auth ? { auth } : {}) }), [apiUrl, auth]);
    const storage = useMemo(() => {
        switch (conversationsMode) {
            case 'local':
                return createLocalStorage();
            case 'disabled':
                return createDisabledStorage();
            case 'persistent':
            default:
                return createServerStorage(copilotClient);
        }
    }, [conversationsMode, copilotClient]);
    const persist = conversationsMode !== 'disabled';
    // Per-session conversation id (new-per-session: starts empty; the store
    // mints an id on the first save, which we reuse for subsequent turns).
    // Refs, not state — the `onTranscript` closure must read/write the
    // latest values without re-subscribing and without forcing re-renders
    // mid-call.
    const conversationIdRef = useRef(undefined);
    const messagesRef = useRef([]);
    // Concatenated non-final assistant deltas, used only as a fallback when
    // the `done` event arrives with an empty body. The `done` event
    // normally carries the full transcript, so this is belt-and-braces.
    const assistantBufRef = useRef('');
    const handleTranscript = useCallback((event) => {
        // Assistant transcripts stream as incremental deltas before a final
        // `done`; accumulate them as a fallback. User transcripts arrive as
        // a single final event, so non-final user deltas never occur today.
        if (event.role === 'assistant' && !event.final) {
            assistantBufRef.current += event.delta;
            return;
        }
        if (!event.final)
            return;
        const raw = event.role === 'assistant' ? event.delta || assistantBufRef.current : event.delta;
        if (event.role === 'assistant')
            assistantBufRef.current = '';
        const content = raw.trim();
        if (!content)
            return;
        const message = {
            role: event.role,
            content,
            timestamp: new Date().toISOString(),
        };
        messagesRef.current = [...messagesRef.current, message];
        if (!persist)
            return;
        // Full-replace save after every finalized turn — same semantics as
        // the chat path. Best-effort: a failed save must not interrupt the
        // live call, and the next turn re-sends the (now longer) history, so
        // a transient failure self-heals.
        void storage
            .save({
            appId,
            ...(conversationIdRef.current ? { id: conversationIdRef.current } : {}),
            title: deriveTitle(messagesRef.current),
            messages: messagesRef.current,
            // Tag the origin so voice sessions are filterable in history
            // while still sharing the chat conversation list.
            metadata: { source: 'voice' },
        })
            .then((saved) => {
            conversationIdRef.current = saved.conversation.id;
        })
            .catch(() => {
            /* swallow — see note above */
        });
    }, [appId, persist, storage]);
    // Enable user-side transcription by default when we intend to persist
    // (otherwise the user turns never arrive). Respect a host-supplied
    // transcription config, and add no STT cost when persistence is off.
    const audioInput = useMemo(() => {
        if (rt.audioInput?.transcription)
            return rt.audioInput;
        if (!persist)
            return rt.audioInput;
        return {
            ...(rt.audioInput ?? {}),
            transcription: { model: DEFAULT_REALTIME_TRANSCRIPTION_MODEL },
        };
    }, [rt.audioInput, persist]);
    return (_jsx(VoiceWidget, { appId: appId, onTranscript: handleTranscript, ...(auth !== undefined ? { auth } : {}), ...(props.apiUrl !== undefined ? { apiUrl: props.apiUrl } : {}), ...(rt.model !== undefined ? { model: rt.model } : {}), ...(rt.instructions !== undefined ? { instructions: rt.instructions } : {}), ...(rt.toolChoice !== undefined ? { toolChoice: rt.toolChoice } : {}), ...(rt.outputModalities !== undefined
            ? { outputModalities: rt.outputModalities }
            : {}), ...(rt.temperature !== undefined ? { temperature: rt.temperature } : {}), ...(rt.maxOutputTokens !== undefined ? { maxOutputTokens: rt.maxOutputTokens } : {}), ...(rt.tracing !== undefined ? { tracing: rt.tracing } : {}), ...(rt.prompt !== undefined ? { prompt: rt.prompt } : {}), ...(rt.include !== undefined ? { include: rt.include } : {}), ...(audioInput !== undefined ? { audioInput } : {}), ...(rt.audioOutput !== undefined ? { audioOutput: rt.audioOutput } : {}), ...(rt.sessionOverrides !== undefined
            ? { sessionOverrides: rt.sessionOverrides }
            : {}), ...(rt.expiresInSeconds !== undefined
            ? { expiresInSeconds: rt.expiresInSeconds }
            : {}), ...(rt.voice !== undefined ? { voice: rt.voice } : {}), ...(rt.maxResponseOutputTokens !== undefined
            ? { maxResponseOutputTokens: rt.maxResponseOutputTokens }
            : {}), ...(rt.maxSessionSeconds !== undefined
            ? { maxSessionSeconds: rt.maxSessionSeconds }
            : {}), ...(props.serverTools !== undefined ? { serverTools: props.serverTools } : {}), ...(props.tools !== undefined ? { tools: props.tools } : {}), ...(props.layout === 'inline' ? { layout: 'inline' } : {}), ...(props.title !== undefined ? { title: props.title } : {}) }));
}
export function Copilot(props) {
    // Realtime mode takes over rendering entirely. Bidirectional voice is
    // a different transport than the chat path (WebRTC vs JSON-RPC), so
    // we delegate to `<VoiceWidget>` and skip every chat-only state hook
    // below. The runtime guard catches hosts that pass both `voice` and
    // `realtime` (TS hosts get a clear error at the call site; CDN hosts
    // that pass raw attributes / properties get the thrown message).
    // `voice="realtime"` keeps the chat panel and uses `realtime` as the
    // in-chat session's tuning, so it skips the voice-only branch. That
    // combination used to throw, so no existing host depends on it.
    if (props.realtime && props.voice !== 'realtime') {
        if (props.voice !== undefined && props.voice !== 'none') {
            throw new Error('Copilot: `voice` and `realtime` are mutually exclusive. ' +
                'The sandwich voice modes (browser / llm) and OpenAI Realtime ' +
                'use different transports — pick one.');
        }
        // Persistence + transcript wiring lives in `<RealtimeCopilot>`, an
        // extracted component so the conversation-store hooks it needs
        // (`useMemo`/`useRef`) sit in their own render and never violate the
        // Rules of Hooks against this early `return`.
        return _jsx(RealtimeCopilot, { ...props });
    }
    const { appId, auth, apiUrl = '/api', model, systemHints, maxRounds, serverTools, tools, voice = 'browser', voiceLang, lang: langProp, strings: stringOverrides, suggestions, header = 'full', layout = 'popup', conversations: conversationsMode = 'persistent', subtitle, enableFileUpload = false, fileUploadAccept = 'image/*,application/pdf,text/plain', maxFileSizeBytes = DEFAULT_MAX_FILE_SIZE_BYTES, enableModelPicker = false, onLayoutChange, realtime: realtimeConfig, } = props;
    // Interface language + string table. `lang` is re-resolved each render
    // (cheap) so a host that flips `<html lang>` and re-renders follows it.
    const lang = resolveLanguage(langProp);
    const s = useMemo(() => buildStrings(lang, stringOverrides), [lang, stringOverrides]);
    const title = props.title ?? s.defaultTitle;
    // Voice defaults to the UI language so Spanish pages dictate in Spanish
    // without a separate `voiceLang`.
    const effectiveVoiceLang = voiceLang ?? lang;
    const isPopup = layout === 'popup';
    const isDocked = layout === 'docked-left' || layout === 'docked-right';
    const dockSide = layout === 'docked-right' ? 'right' : 'left';
    const historyEnabled = conversationsMode !== 'disabled';
    useEffect(() => {
        ensureStylesInjected();
    }, []);
    const [open, setOpen] = useState(false);
    const [view, setView] = useState('chat');
    // Phone-sized viewport → the popup renders full screen and ignores the
    // user's dragged/resized rect (which is kept for when the window grows).
    const [isCompactViewport, setIsCompactViewport] = useState(false);
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return;
        const mq = window.matchMedia(`(max-width: ${COMPACT_VIEWPORT_MAX_PX}px)`);
        const update = () => setIsCompactViewport(mq.matches);
        update();
        // Safari < 14 only has the deprecated addListener/removeListener.
        if (typeof mq.addEventListener === 'function') {
            mq.addEventListener('change', update);
            return () => mq.removeEventListener('change', update);
        }
        mq.addListener(update);
        return () => mq.removeListener(update);
    }, []);
    const popupFullscreen = isPopup && isCompactViewport;
    // Docked-mode geometry. Hydrate from localStorage once on mount so the
    // user's chosen width and collapsed state survive reloads. Reads are
    // gated on `isDocked` to avoid touching storage at all from popup /
    // inline hosts. Width is in CSS pixels; null means "no user override
    // yet — fall back to the CSS default" and lets the panel inherit
    // `min(22rem, 100vw)` from the stylesheet.
    const [dockCollapsed, setDockCollapsed] = useState(false);
    // Docked-mode "closed" state. Distinct from `dockCollapsed` (which docks
    // to a thin rail): closing fully hides the panel *and* the rail and
    // reveals the floating launcher so the user can reopen it. Deliberately
    // NOT persisted (unlike collapse/width) — a reload always restores the
    // open docked panel so a user can never "lose" the widget permanently.
    const [dockedClosed, setDockedClosed] = useState(false);
    const [dockWidthPx, setDockWidthPx] = useState(null);
    const [resizing, setResizing] = useState(false);
    // Holds the drag-start pointer X and starting panel width so pointermove
    // can compute the new width without re-reading the DOM each frame.
    const resizeStartRef = useRef(null);
    // Popup-mode floating-window rect. `null` = not yet moved/resized, so the
    // panel renders at the CSS default (anchored bottom-right); the first
    // gesture promotes it to an explicit rect. Persisted per appId and
    // re-clamped to the viewport on load and on window resize.
    const [popupRect, setPopupRect] = useState(null);
    // 'move' = header drag, 'resize' = edge/corner drag, null = idle. Drives
    // the active-cursor styling and the gesture handlers.
    const [popupGesture, setPopupGesture] = useState(null);
    const panelRef = useRef(null);
    // Snapshot taken on pointerdown so pointermove can compute the new rect
    // from the start without re-reading the DOM each frame.
    const popupGestureStartRef = useRef(null);
    useEffect(() => {
        if (!isDocked)
            return;
        const persisted = readPersistedDockState(appId);
        if (persisted) {
            setDockCollapsed(persisted.collapsed);
            // Re-clamp on hydration: the stored width may exceed the current
            // viewport (smaller screen than where the user last set it).
            const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
            setDockWidthPx(clampDockWidth(persisted.widthPx, vw));
        }
    }, [appId, isDocked]);
    // Hydrate the persisted popup rect once on mount (popup only), re-clamped
    // to the current viewport so a window saved on a bigger screen is fitted
    // (size AND position) instead of spilling off-screen. If clamping changed
    // it, persist the fitted rect so the next load starts valid.
    useEffect(() => {
        if (!isPopup)
            return;
        const persisted = readPersistedPopupRect(appId);
        if (!persisted)
            return;
        const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
        const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
        const fitted = clampPopupRect(persisted, vw, vh);
        setPopupRect(fitted);
        if (fitted.left !== persisted.left ||
            fitted.top !== persisted.top ||
            fitted.width !== persisted.width ||
            fitted.height !== persisted.height) {
            writePersistedPopupRect(appId, fitted);
        }
    }, [appId, isPopup]);
    // Keep a custom popup window inside the viewport as the window shrinks.
    // Re-clamps the live rect only; the stored preference is left untouched so
    // the mount effect can re-fit it next load.
    useEffect(() => {
        if (!isPopup || typeof window === 'undefined' || popupRect === null)
            return;
        const onResize = () => {
            setPopupRect((prev) => prev ? clampPopupRect(prev, window.innerWidth, window.innerHeight) : prev);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [isPopup, popupRect]);
    // Publish the current docked geometry as a CSS custom property on
    // `<html>` so hosts can react to width changes purely in CSS:
    //   main { margin-left: var(--ada-copilot-dock-width, 0); }
    // The variable is cleared when the widget is unmounted or switches
    // out of a docked layout so the host's `var(..., 0)` fallback wins.
    useEffect(() => {
        if (typeof document === 'undefined')
            return;
        if (!isDocked) {
            document.documentElement.style.removeProperty('--ada-copilot-dock-width');
            return;
        }
        const px = dockCollapsed ? RAIL_WIDTH_PX : (dockWidthPx ?? DEFAULT_DOCK_WIDTH_PX);
        document.documentElement.style.setProperty('--ada-copilot-dock-width', `${px}px`);
        return () => {
            document.documentElement.style.removeProperty('--ada-copilot-dock-width');
        };
    }, [isDocked, dockCollapsed, dockWidthPx]);
    // Resize gesture. Uses Pointer Events so it works with mouse, touch,
    // and pen with one code path. `setPointerCapture` keeps the move/up
    // events flowing even if the pointer leaves the 6 px handle during
    // the drag — without it, dragging fast off the edge would silently
    // cancel the gesture mid-swipe.
    const onResizePointerDown = useCallback((e) => {
        if (!isDocked)
            return;
        e.preventDefault();
        const startWidth = dockWidthPx ?? DEFAULT_DOCK_WIDTH_PX;
        resizeStartRef.current = { pointerX: e.clientX, startWidth };
        e.currentTarget.setPointerCapture(e.pointerId);
        setResizing(true);
    }, [isDocked, dockWidthPx]);
    const onResizePointerMove = useCallback((e) => {
        if (!resizing)
            return;
        const start = resizeStartRef.current;
        if (!start)
            return;
        // Left-docked grows when the pointer moves right; right-docked
        // grows when the pointer moves left — the panel's free edge is
        // the one the user is dragging in both cases.
        const delta = dockSide === 'left' ? e.clientX - start.pointerX : start.pointerX - e.clientX;
        const next = clampDockWidth(start.startWidth + delta, window.innerWidth);
        setDockWidthPx(next);
    }, [resizing, dockSide]);
    const onResizePointerUp = useCallback((e) => {
        if (!resizing)
            return;
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        catch {
            // Pointer was never captured (e.g. up fired before move) —
            // releasePointerCapture throws in that case; harmless.
        }
        resizeStartRef.current = null;
        setResizing(false);
        const finalWidth = dockWidthPx ?? DEFAULT_DOCK_WIDTH_PX;
        writePersistedDockState(appId, { collapsed: dockCollapsed, widthPx: finalWidth });
        onLayoutChange?.({ side: dockSide, collapsed: dockCollapsed, widthPx: finalWidth });
    }, [resizing, dockWidthPx, dockCollapsed, appId, dockSide, onLayoutChange]);
    // Popup-mode floating-window gestures (header drag to move, edge/corner
    // drag to resize). One Pointer Events code path for mouse/touch/pen, with
    // setPointerCapture so a fast drag off the handle doesn't drop the gesture.
    //
    // The start rect is captured from `popupRect` if set, else from the live
    // DOM rect — this is what "promotes" a still-default (anchored) panel to
    // an explicit free-floating rect on the user's very first gesture.
    const beginPopupGesture = useCallback((e, mode, dir) => {
        if (!isPopup)
            return;
        e.preventDefault();
        const dom = panelRef.current?.getBoundingClientRect();
        const startRect = popupRect ?? {
            left: dom?.left ?? 0,
            top: dom?.top ?? 0,
            width: dom?.width ?? DEFAULT_POPUP_WIDTH_PX,
            height: dom?.height ?? DEFAULT_POPUP_HEIGHT_PX,
        };
        popupGestureStartRef.current = {
            pointerX: e.clientX,
            pointerY: e.clientY,
            rect: startRect,
            mode,
            dir,
        };
        // Promote to an explicit rect immediately so this frame already
        // renders via left/top/width/height instead of the anchored CSS.
        setPopupRect(startRect);
        e.currentTarget.setPointerCapture(e.pointerId);
        setPopupGesture(mode);
    }, [isPopup, popupRect]);
    const onPopupHeaderPointerDown = useCallback((e) => {
        if (!isPopup || popupFullscreen)
            return;
        // Don't hijack clicks on the header's own controls (mic/speaker/
        // history/＋/✕) — only blank header area starts a move.
        if (e.target.closest('button'))
            return;
        beginPopupGesture(e, 'move', { x: 0, y: 0 });
    }, [isPopup, popupFullscreen, beginPopupGesture]);
    const onPopupResizePointerDown = useCallback((e, dir) => beginPopupGesture(e, 'resize', dir), [beginPopupGesture]);
    const onPopupGesturePointerMove = useCallback((e) => {
        const start = popupGestureStartRef.current;
        if (!start)
            return;
        const dx = e.clientX - start.pointerX;
        const dy = e.clientY - start.pointerY;
        const s = start.rect;
        let next;
        if (start.mode === 'move') {
            next = { left: s.left + dx, top: s.top + dy, width: s.width, height: s.height };
        }
        else {
            const right = s.left + s.width;
            const bottom = s.top + s.height;
            let { left, top, width, height } = s;
            if (start.dir.x === 1)
                width = s.width + dx;
            if (start.dir.x === -1) {
                left = s.left + dx;
                width = right - left;
            }
            if (start.dir.y === 1)
                height = s.height + dy;
            if (start.dir.y === -1) {
                top = s.top + dy;
                height = bottom - top;
            }
            // Enforce the minimum while keeping the dragged-against edge fixed.
            if (width < MIN_POPUP_WIDTH_PX) {
                width = MIN_POPUP_WIDTH_PX;
                if (start.dir.x === -1)
                    left = right - MIN_POPUP_WIDTH_PX;
            }
            if (height < MIN_POPUP_HEIGHT_PX) {
                height = MIN_POPUP_HEIGHT_PX;
                if (start.dir.y === -1)
                    top = bottom - MIN_POPUP_HEIGHT_PX;
            }
            next = { left, top, width, height };
        }
        setPopupRect(clampPopupRect(next, window.innerWidth, window.innerHeight));
    }, []);
    const onPopupGesturePointerUp = useCallback((e) => {
        if (!popupGestureStartRef.current)
            return;
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        catch {
            // Pointer was never captured (up before move) — harmless.
        }
        popupGestureStartRef.current = null;
        setPopupGesture(null);
        setPopupRect((prev) => {
            if (!prev)
                return prev;
            const fitted = clampPopupRect(prev, window.innerWidth, window.innerHeight);
            writePersistedPopupRect(appId, fitted);
            return fitted;
        });
    }, [appId]);
    const toggleDockCollapsed = useCallback(() => {
        setDockCollapsed((prev) => {
            const next = !prev;
            const widthPx = dockWidthPx ?? DEFAULT_DOCK_WIDTH_PX;
            writePersistedDockState(appId, { collapsed: next, widthPx });
            onLayoutChange?.({
                side: dockSide,
                collapsed: next,
                widthPx: next ? RAIL_WIDTH_PX : widthPx,
            });
            return next;
        });
    }, [appId, dockWidthPx, dockSide, onLayoutChange]);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState('');
    const [pending, setPending] = useState(false);
    const [error, setError] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    // Streaming-turn state. `streamingText` accumulates chunks for the live
    // bubble; `toolStatus` shows a one-line "calling X" between rounds.
    // Both reset on each new turn and clear when the `final` event arrives.
    const [streamingText, setStreamingText] = useState('');
    const [toolStatus, setToolStatus] = useState(null);
    const messagesRef = useRef(null);
    const inputRef = useRef(null);
    // Shown after the user presses Stop; cleared on the next send.
    const [stoppedNotice, setStoppedNotice] = useState(false);
    // Mirrors of state read from long-lived async code (the stream loop,
    // speech callbacks) where the render-time closure would be stale.
    const pendingRef = useRef(false);
    const streamingTextRef = useRef('');
    // The in-flight turn. Stop aborts its controller and clears the ref;
    // the stream loop checks `turnRef.current?.id` after every event and
    // bails out silently once its turn is no longer current.
    const turnRef = useRef(null);
    const turnCounterRef = useRef(0);
    // Set by the voice-conversation code (defined further down, since it
    // calls `submit`) so a failed turn can end the hands-free loop instead
    // of leaving it waiting for a reply that will never be spoken.
    const voiceChatOnTurnErrorRef = useRef(() => { });
    const endVoiceChatRef = useRef(() => { });
    // The in-chat realtime voice session (`voice="realtime"`), created
    // further down; read here by `submit` / `startNew`, which come first.
    const talkRef = useRef(null);
    // Latest committed messages, for voice turns that arrive between renders.
    const messagesNowRef = useRef([]);
    // Info line after the voice session closed itself (inactivity).
    const [talkNotice, setTalkNotice] = useState(null);
    // Set by Stop when it commits a partial reply, so that reply is not
    // read aloud by the speaker / Talk-mode effect.
    const skipSpeakRef = useRef(false);
    // Index of the last assistant message read aloud (speaker / Talk mode).
    // Reset whenever the message list is replaced (new / loaded conversation),
    // otherwise a shorter list's replies would never be spoken.
    const lastSpokenRef = useRef(-1);
    // Conversation identity for persistence. Every fresh or loaded
    // conversation is an "epoch" with its own id slot. Saves run one at a
    // time through a promise chain and read their epoch's id when they run,
    // so a turn's save can finish in the background (the composer is
    // released as soon as the reply is committed) without two first-saves
    // minting two conversations, and without a late save from a previous
    // conversation re-pointing the current one.
    const epochRef = useRef(0);
    const epochIdsRef = useRef(new Map([[0, null]]));
    const saveChainRef = useRef(Promise.resolve());
    const beginConversation = useCallback((id) => {
        epochRef.current += 1;
        epochIdsRef.current.set(epochRef.current, id);
    }, []);
    // Auto-scroll only while the user is at (or near) the bottom, so reading
    // an earlier message isn't interrupted by streaming chunks.
    const stickToBottomRef = useRef(true);
    // Focus management. Requests are applied in an effect (after commit) so
    // the textarea exists even when the request coincides with a view
    // switch. 'soft' only moves focus when it is inside the widget or on
    // nothing (body) — it never steals focus from the host page; 'force'
    // is for explicit user gestures on widget controls.
    const focusModeRef = useRef(null);
    const [focusTick, setFocusTick] = useState(0);
    const requestFocus = useCallback((mode) => {
        focusModeRef.current = mode;
        setFocusTick((t) => t + 1);
    }, []);
    useEffect(() => {
        const mode = focusModeRef.current;
        if (!mode)
            return;
        focusModeRef.current = null;
        const el = inputRef.current;
        if (!el)
            return;
        if (mode === 'soft') {
            // Inside a shadow root, `document.activeElement` is the host
            // element; the root node's own `activeElement` is the real target.
            const rootNode = el.getRootNode();
            const active = rootNode.activeElement;
            const insideWidget = !!active && !!panelRef.current?.contains(active);
            const docActive = document.activeElement;
            const onNothing = !docActive || docActive === document.body || docActive === document.documentElement;
            if (!insideWidget && !onNothing)
                return;
        }
        el.focus({ preventScroll: true });
        const end = el.value.length;
        try {
            el.setSelectionRange(end, end);
        }
        catch {
            // Some input types reject selection APIs; harmless for a textarea.
        }
    }, [focusTick]);
    // File-upload state. `attachments` buffers files the user has picked
    // (via the clip button or drag-and-drop) but not yet submitted; they
    // travel with the next user turn and get cleared once `submit()`
    // completes. `dragging` drives the visual outline on the composer
    // while a drag is hovering over it.
    const [attachments, setAttachments] = useState([]);
    const [dragging, setDragging] = useState(false);
    const fileInputRef = useRef(null);
    // Cached model catalog used by the picker (when enabled) and by the
    // capability gate that hides the clip button on text-only models. The
    // list is fetched once per `llm` instance on mount; failures are
    // silent (no picker, no gating — same as if the flag were off). The
    // gate is conservative: until the list resolves, the clip button
    // stays hidden, because we'd rather hide it incorrectly than show a
    // capability the model rejects with a wire-level error.
    const [models, setModels] = useState([]);
    // The model the next turn will use. Seeded from the `model` prop;
    // mutated by the picker when {@link enableModelPicker} is on. The
    // user's choice wins over later changes to the `model` prop — the
    // picker would otherwise feel broken (clicking a model and watching
    // it revert is the worst kind of UX).
    const [currentModel, setCurrentModel] = useState(model);
    const userPickedModelRef = useRef(false);
    useEffect(() => {
        if (userPickedModelRef.current)
            return;
        setCurrentModel(model);
    }, [model]);
    // The widget creates both clients internally so hosts only have to
    // think about auth. Memoised so the same instances survive re-renders
    // (and so the driver's `useMemo` deps remain stable).
    const llm = useMemo(() => new LlmClient({ url: apiUrl, ...(auth ? { auth } : {}) }), [apiUrl, auth]);
    const copilotClient = useMemo(() => new CopilotClient({ url: apiUrl, ...(auth ? { auth } : {}) }), [apiUrl, auth]);
    // Storage backend for conversation history. Three modes share one
    // interface so the React code below is mode-agnostic; the `'persistent'`
    // mode is the only one that actually talks to the server.
    const storage = useMemo(() => {
        switch (conversationsMode) {
            case 'local':
                return createLocalStorage();
            case 'disabled':
                return createDisabledStorage();
            case 'persistent':
            default:
                return createServerStorage(copilotClient);
        }
    }, [conversationsMode, copilotClient]);
    // Fetch the model catalog once per LlmClient. Failures are silent —
    // an unauthenticated host or a 5xx leaves `models` empty, which
    // disables both the picker (no rows to render) and the capability
    // gate (the clip button stays hidden). Cancel on unmount so a slow
    // response can't set state on a dead component.
    useEffect(() => {
        if (!enableFileUpload && !enableModelPicker)
            return;
        let cancelled = false;
        void (async () => {
            try {
                const result = await llm.model.list();
                if (!cancelled)
                    setModels(result.models);
            }
            catch {
                // Best-effort; see the JSDoc above.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [llm, enableFileUpload, enableModelPicker]);
    // Resolve the active model (following alias chains in the cached
    // list) to its `capabilities` block, so the composer can decide
    // whether to surface the clip button. Returns `undefined` for
    // unknown keys, alias cycles, or empty catalogs — every caller
    // treats that as "assume not supported".
    const activeModelKey = currentModel ?? DEFAULT_MODEL_KEY;
    const canAttachFiles = useMemo(() => {
        if (!enableFileUpload)
            return false;
        const caps = resolveCapabilities(models, activeModelKey);
        if (!caps)
            return false;
        return caps.fileInput === true || caps.imageInput === true;
    }, [enableFileUpload, models, activeModelKey]);
    const driver = useMemo(() => {
        const registry = new ClientToolRegistry();
        for (const tool of tools ?? [])
            registry.register(tool);
        return new ConversationDriver({
            llm,
            appId,
            toolRegistry: registry,
            ...(currentModel ? { model: currentModel } : {}),
            ...(systemHints ? { systemHints } : {}),
            ...(maxRounds !== undefined ? { maxRounds } : {}),
            ...(serverTools !== undefined ? { serverTools } : {}),
            locale: lang,
        });
    }, [llm, appId, currentModel, systemHints, maxRounds, serverTools, tools, lang]);
    // User-facing status lines for client tools (`ClientToolDescriptor.label`).
    // Tools without a label fall back to the generic localized line — the
    // raw snake_case name is never shown.
    const toolLabels = useMemo(() => {
        const map = new Map();
        for (const t of tools ?? [])
            if (t.label)
                map.set(t.name, t.label);
        return map;
    }, [tools]);
    // Auto-scroll to the latest message when the list grows OR when
    // streaming chunks land — but only while the user is near the bottom.
    useEffect(() => {
        const el = messagesRef.current;
        if (!el || !stickToBottomRef.current)
            return;
        el.scrollTop = el.scrollHeight;
    }, [messages, pending, streamingText, toolStatus, stoppedNotice, error]);
    const onMessagesScroll = useCallback(() => {
        const el = messagesRef.current;
        if (!el)
            return;
        stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    }, []);
    // Grow the text box with its content (CSS max-height caps it, then it
    // scrolls). Border-box sizing, so add the borders back to scrollHeight.
    useEffect(() => {
        const el = inputRef.current;
        if (!el)
            return;
        const borders = el.offsetHeight - el.clientHeight;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight + borders}px`;
    }, [draft, view]);
    // Queue a full-replace save of `history` for the current conversation.
    // Failures are surfaced inline; the chat keeps working.
    const enqueueSave = useCallback((history) => {
        const epoch = epochRef.current;
        saveChainRef.current = saveChainRef.current.then(async () => {
            const id = epochIdsRef.current.get(epoch) ?? null;
            try {
                const saved = await storage.save({
                    appId,
                    ...(id ? { id } : {}),
                    title: deriveTitle(history, s.untitledConversation),
                    // Strip attachment bytes before they hit persistent
                    // storage — base64 PDFs would blow `localStorage`'s
                    // quota in two turns and bloat server-side history for
                    // little gain. The user-visible bubble keeps a
                    // `[file: <name>]` line so a reloaded conversation
                    // still shows that a file was sent.
                    messages: stripAttachmentsForStorage(history),
                });
                epochIdsRef.current.set(epoch, saved.conversation.id);
            }
            catch (persistErr) {
                setError(formatString(s.persistFailed, { error: formatError(persistErr) }));
            }
        });
    }, [storage, appId, s]);
    // Clear every trace of the in-flight turn from the UI. Callers decide
    // what (if anything) to commit first.
    const settleTurn = useCallback(() => {
        turnRef.current = null;
        pendingRef.current = false;
        streamingTextRef.current = '';
        setStreamingText('');
        setToolStatus(null);
        setPending(false);
    }, []);
    // Drop an in-flight turn without keeping its partial text (used when
    // the message list is about to be replaced).
    const abandonTurn = useCallback(() => {
        const turn = turnRef.current;
        if (!turn)
            return;
        turn.controller.abort();
        settleTurn();
    }, [settleTurn]);
    const submit = useCallback(async (text) => {
        const trimmed = text.trim();
        // Allow submit with attachments and an empty text body: a user
        // may drop a file and ask "what is this?" without typing — the
        // file alone is a meaningful prompt. The driver guards against
        // an empty text part in that case.
        if (!trimmed && attachments.length === 0)
            return;
        if (pendingRef.current)
            return;
        // While a realtime voice session is open, typed text goes into it
        // and is answered by voice; the turn lands in the chat through the
        // session. Attachments stay in the tray for the next text turn.
        const talk = talkRef.current;
        if (talk?.active) {
            if (!trimmed)
                return;
            setError(null);
            setDraft('');
            stickToBottomRef.current = true;
            talk.sendText(trimmed);
            return;
        }
        setError(null);
        setStoppedNotice(false);
        const userMsg = {
            role: 'user',
            content: trimmed,
            timestamp: new Date().toISOString(),
            ...(attachments.length > 0 ? { attachments } : {}),
        };
        const next = [...messages, userMsg];
        setMessages(next);
        setDraft('');
        // Clear the pending attachments buffer immediately. The bytes
        // are still on `userMsg` so the LLM call and the in-memory
        // history retain them; only the composer's "next turn" tray
        // resets.
        setAttachments([]);
        stickToBottomRef.current = true;
        const controller = new AbortController();
        const turnId = ++turnCounterRef.current;
        turnRef.current = { id: turnId, controller, history: next };
        pendingRef.current = true;
        setPending(true);
        streamingTextRef.current = '';
        setStreamingText('');
        setToolStatus(null);
        const isCurrent = () => turnRef.current?.id === turnId;
        let finalText = '';
        try {
            for await (const event of driver.stream(next, { signal: controller.signal })) {
                // Stopped (or superseded by "new conversation"): the stop
                // handler already settled the UI, so drop everything else.
                if (!isCurrent())
                    return;
                if (event.type === 'chunk') {
                    // Chunks always belong to the final round; clear any
                    // tool-status carried over from a prior round.
                    setToolStatus(null);
                    streamingTextRef.current += event.text;
                    setStreamingText(streamingTextRef.current);
                }
                else if (event.type === 'tool-call') {
                    // Text streamed before a tool round is a preamble ("let me
                    // check…") that the final message won't contain — drop it so
                    // the bubble doesn't jump when the answer is committed.
                    streamingTextRef.current = '';
                    setStreamingText('');
                    const labels = [
                        ...new Set(event.toolCalls
                            .map((c) => toolLabels.get(c.name))
                            .filter((l) => Boolean(l))),
                    ];
                    setToolStatus(labels.length > 0 ? labels.join(' · ') : s.toolWorking);
                }
                else if (event.type === 'final') {
                    finalText = event.text;
                }
            }
            if (!isCurrent())
                return;
            const assistantMsg = {
                role: 'assistant',
                content: finalText,
                timestamp: new Date().toISOString(),
            };
            const fullHistory = [...next, assistantMsg];
            // Release the composer together with the commit: the save runs in
            // the background (serialized by `enqueueSave`), so Stop can no
            // longer race it and Talk mode can listen again straight away.
            settleTurn();
            setMessages(fullHistory);
            enqueueSave(fullHistory);
            requestFocus('soft');
        }
        catch (err) {
            if (!isCurrent())
                return;
            setError(formatString(s.genericError, { error: formatError(err) }));
            voiceChatOnTurnErrorRef.current();
        }
        finally {
            if (isCurrent()) {
                settleTurn();
                requestFocus('soft');
            }
        }
    }, [attachments, driver, messages, enqueueSave, settleTurn, requestFocus, s, toolLabels]);
    // Stop the in-flight turn. The request is aborted client-side (the
    // server still finishes — and bills — the current generation). Any text
    // already streamed is kept as the assistant's reply; if nothing arrived
    // yet, the user's question simply stays unanswered in the history.
    const stop = useCallback(() => {
        const turn = turnRef.current;
        if (!turn)
            return;
        turn.controller.abort();
        const partial = streamingTextRef.current.trim();
        settleTurn();
        if (partial) {
            const fullHistory = [
                ...turn.history,
                { role: 'assistant', content: partial, timestamp: new Date().toISOString() },
            ];
            skipSpeakRef.current = true;
            setMessages(fullHistory);
            enqueueSave(fullHistory);
        }
        setStoppedNotice(true);
        stopSpeaking();
        requestFocus('soft');
    }, [settleTurn, enqueueSave, requestFocus]);
    // Read a single browser `File` into the shape `CopilotMessage`
    // expects. We use `FileReader.readAsDataURL` for two reasons:
    //   1. The resulting `data:<mime>;base64,...` string round-trips
    //      through `LLMImagePart`/`LLMFilePart` unchanged — the LLM
    //      service detects the prefix and decodes to bytes on the
    //      server side.
    //   2. We never need the raw bytes client-side, so avoiding
    //      `ArrayBuffer` / `Blob` plumbing keeps the helper terse.
    // Rejects on the standard FileReader error path; callers turn the
    // rejection into an inline `setError`.
    const readFileAsAttachment = useCallback((file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const data = reader.result;
                if (typeof data !== 'string') {
                    reject(new Error(`Could not read "${file.name}" as a data URI`));
                    return;
                }
                resolve({
                    name: file.name,
                    mimeType: file.type || 'application/octet-stream',
                    data,
                    sizeBytes: file.size,
                });
            };
            reader.onerror = () => reject(reader.error ?? new Error(`Failed to read "${file.name}"`));
            reader.readAsDataURL(file);
        });
    }, []);
    // Add a batch of files to the pending-attachments tray. Files that
    // exceed `maxFileSizeBytes` are dropped with an inline error
    // listing the offenders (so the user knows which file the toast is
    // about). The function tolerates a partial-success path: small
    // files still land in the tray even if a sibling was too big.
    const addFiles = useCallback(async (files) => {
        if (files.length === 0)
            return;
        const oversized = [];
        const accepted = [];
        for (const f of files) {
            if (f.size > maxFileSizeBytes) {
                oversized.push(f.name);
            }
            else {
                accepted.push(f);
            }
        }
        if (oversized.length > 0) {
            setError(formatString(s.filesTooLarge, {
                names: oversized.join(', '),
                limit: formatBytes(maxFileSizeBytes),
            }));
        }
        try {
            const next = await Promise.all(accepted.map(readFileAsAttachment));
            setAttachments((prev) => [...prev, ...next]);
        }
        catch (err) {
            setError(formatError(err));
        }
    }, [maxFileSizeBytes, readFileAsAttachment, s]);
    const removeAttachment = useCallback((index) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    }, []);
    const onFileInputChange = useCallback((e) => {
        const files = e.target.files ? Array.from(e.target.files) : [];
        void addFiles(files);
        // Reset so picking the same file twice in a row still fires
        // `change` — browsers de-dup identical paths otherwise.
        e.target.value = '';
    }, [addFiles]);
    const onDragOver = useCallback((e) => {
        if (!canAttachFiles)
            return;
        // Required to make the drop event fire at all. Calling it
        // unconditionally would also block text-drag selection inside
        // the textarea, so the `canAttachFiles` early-return matters.
        e.preventDefault();
        setDragging(true);
    }, [canAttachFiles]);
    const onDragLeave = useCallback((e) => {
        // `dragleave` fires when the cursor crosses any child boundary —
        // checking `currentTarget.contains(relatedTarget)` keeps the
        // visual outline stable while the pointer moves between the
        // textarea, chip row, and buttons inside the form.
        const next = e.relatedTarget;
        if (next instanceof Node &&
            e.currentTarget.contains(next)) {
            return;
        }
        setDragging(false);
    }, []);
    const onDrop = useCallback((e) => {
        if (!canAttachFiles)
            return;
        e.preventDefault();
        setDragging(false);
        const files = e.dataTransfer.files
            ? Array.from(e.dataTransfer.files)
            : [];
        void addFiles(files);
    }, [addFiles, canAttachFiles]);
    const onPickModel = useCallback((key) => {
        userPickedModelRef.current = true;
        setCurrentModel(key);
    }, []);
    const onSubmit = useCallback((e) => {
        e.preventDefault();
        void submit(draft);
    }, [draft, submit]);
    const onKeyDown = useCallback((e) => {
        // Enter while an IME is composing (Japanese, Chinese, accented
        // input on some layouts) confirms the composition — never send.
        if (e.nativeEvent.isComposing || e.keyCode === 229)
            return;
        if (e.key === 'Enter' && !e.shiftKey) {
            // Swallow Enter even while a turn is in flight (no stray newline);
            // `submit` itself ignores the call until the reply lands.
            e.preventDefault();
            void submit(draft);
        }
    }, [draft, submit]);
    const startNew = useCallback(() => {
        abandonTurn();
        endVoiceChatRef.current();
        talkRef.current?.end();
        stopSpeaking();
        beginConversation(null);
        lastSpokenRef.current = -1;
        setMessages([]);
        setError(null);
        setStoppedNotice(false);
        setTalkNotice(null);
        setView('chat');
        requestFocus('force');
    }, [abandonTurn, beginConversation, requestFocus]);
    const openHistory = useCallback(async () => {
        setView('history');
        setHistoryLoading(true);
        setError(null);
        try {
            const result = await storage.list({ appId });
            setConversations(result.conversations);
        }
        catch (err) {
            setError(formatString(s.genericError, { error: formatError(err) }));
        }
        finally {
            setHistoryLoading(false);
        }
    }, [appId, storage, s]);
    // Per-appId "read replies aloud" preference (default OFF, persisted in
    // localStorage). The mic needs no stored preference: Dictate and Talk
    // are explicit buttons, and the browser's own permission prompt is the
    // consent step. `voice="none"` hides every voice affordance.
    const voiceStorageKey = `ada-copilot-voice:${appId}`;
    const [speakerEnabled, setSpeakerEnabled] = useState(() => readSpeakerPref(voiceStorageKey));
    useEffect(() => {
        writeSpeakerPref(voiceStorageKey, speakerEnabled);
    }, [voiceStorageKey, speakerEnabled]);
    // What the active recognition session is for: 'dictation' types the
    // words into the box (after whatever was already typed) and never sends;
    // 'conversation' (Talk mode) sends the utterance when the user stops.
    const sttModeRef = useRef('dictation');
    const dictationBaseRef = useRef('');
    // Talk mode. `voiceChatRef` mirrors the state for speech callbacks that
    // outlive the render that created them.
    const [voiceChat, setVoiceChat] = useState(false);
    const voiceChatRef = useRef(false);
    const [voiceChatSpeaking, setVoiceChatSpeaking] = useState(false);
    const describeVoiceError = useCallback((code) => {
        switch (code) {
            case 'not-allowed':
            case 'service-not-allowed':
                return s.voiceErrorNotAllowed;
            case 'audio-capture':
                return s.voiceErrorNoMic;
            case 'network':
                return s.voiceErrorNetwork;
            default:
                return formatString(s.voiceErrorGeneric, { error: code });
        }
    }, [s]);
    const stt = useSpeechToText({
        lang: effectiveVoiceLang,
        onInterim: (interim) => {
            setDraft(sttModeRef.current === 'dictation'
                ? joinDictation(dictationBaseRef.current, interim)
                : interim);
        },
        onFinal: (transcript) => {
            if (sttModeRef.current === 'dictation') {
                // Dictation only fills the box — the user reviews and sends.
                setDraft(joinDictation(dictationBaseRef.current, transcript));
                requestFocus('force');
            }
            else if (pendingRef.current) {
                // Shouldn't happen (the mic re-opens only after the reply), but
                // never drop what the user said: park it in the box and stop.
                setDraft(transcript);
                endVoiceChatRef.current();
            }
            else {
                setDraft('');
                void submit(transcript);
            }
        },
        onNoResult: () => {
            if (sttModeRef.current === 'dictation')
                setDraft(dictationBaseRef.current);
            // Silence in Talk mode ends the conversation.
            else
                endVoiceChatRef.current();
        },
        onError: (code) => {
            if (sttModeRef.current === 'dictation')
                setDraft(dictationBaseRef.current);
            else
                endVoiceChatRef.current();
            // Silence isn't worth an error banner.
            if (code !== 'no-speech')
                setError(describeVoiceError(code));
        },
    });
    // `'realtime'` keeps the browser's Dictate and read-aloud toggle; only
    // Talk changes transport.
    const realtimeVoice = voice === 'realtime';
    const voiceOn = voice === 'browser' || realtimeVoice;
    const sttSupported = voiceOn && stt.supported;
    const ttsSupported = voiceOn && speechSynthesisSupported();
    const ttsActive = ttsSupported && speakerEnabled;
    const voiceChatSupported = !realtimeVoice && sttSupported && ttsSupported;
    const dictating = stt.listening && sttModeRef.current === 'dictation';
    const { start: sttStart, abort: sttAbort, stop: sttStop } = stt;
    const toggleDictation = useCallback(() => {
        if (stt.listening) {
            sttStop();
            return;
        }
        if (voiceChatRef.current || talkRef.current?.active)
            return;
        sttModeRef.current = 'dictation';
        dictationBaseRef.current = draft;
        stopSpeaking();
        setError(null);
        sttStart();
    }, [stt.listening, sttStop, sttStart, draft]);
    const listenForVoiceChat = useCallback(() => {
        if (!voiceChatRef.current)
            return;
        sttModeRef.current = 'conversation';
        setVoiceChatSpeaking(false);
        sttStart();
    }, [sttStart]);
    const endVoiceChat = useCallback(() => {
        if (!voiceChatRef.current)
            return;
        voiceChatRef.current = false;
        setVoiceChat(false);
        setVoiceChatSpeaking(false);
        sttAbort();
        stopSpeaking();
    }, [sttAbort]);
    endVoiceChatRef.current = endVoiceChat;
    voiceChatOnTurnErrorRef.current = endVoiceChat;
    // ── In-chat realtime voice (`voice="realtime"`, opt-in) ──
    messagesNowRef.current = messages;
    // Append finished spoken turns to the chat and its saved history.
    const commitTalkTurns = useCallback((turns) => {
        const timestamp = new Date().toISOString();
        const added = turns.map((t) => ({
            role: t.role,
            // A cut-off reply is marked; the ellipsis only when the text was
            // cut mid-sentence.
            content: t.interrupted
                ? `${t.content}${/[.!?…]$/.test(t.content) ? '' : ' …'} _(${s.talkInterrupted})_`
                : t.content,
            timestamp,
        }));
        const next = [...messagesNowRef.current, ...added];
        messagesNowRef.current = next;
        // Already heard: never read these again with the browser voice.
        lastSpokenRef.current = next.length - 1;
        stickToBottomRef.current = true;
        setMessages(next);
        enqueueSave(next);
    }, [enqueueSave, s]);
    const onTalkError = useCallback((err) => {
        if (err.kind === 'dropped') {
            setError(s.talkDropped);
        }
        else if (err.kind === 'start') {
            if (/NotAllowed|Permission|denied/i.test(err.message))
                setError(s.voiceErrorNotAllowed);
            else if (/NotFound|device not found/i.test(err.message))
                setError(s.voiceErrorNoMic);
            else
                setError(formatString(s.talkStartFailed, { error: err.message }));
        }
        else {
            setError(formatString(s.talkSessionError, { error: err.message }));
        }
    }, [s]);
    const talkBasePrompt = useMemo(() => assembleCopilotSystemPrompt({
        appId,
        locale: lang,
        ...(systemHints ? { systemHints } : {}),
    }), [appId, lang, systemHints]);
    const talkLanguage = effectiveVoiceLang.toLowerCase().split(/[-_]/)[0] ?? '';
    const talk = useRealtimeTalk({
        llm,
        ...(tools !== undefined ? { tools } : {}),
        ...(serverTools !== undefined ? { serverTools } : {}),
        basePrompt: talkBasePrompt,
        getHistory: () => messagesNowRef.current,
        ...(realtimeConfig !== undefined ? { config: realtimeConfig } : {}),
        ...(talkLanguage ? { transcriptionLanguage: talkLanguage } : {}),
        toolLabels,
        genericToolLabel: s.toolWorking,
        onTurns: commitTalkTurns,
        onError: onTalkError,
        onIdleEnd: () => setTalkNotice(s.talkIdleEnded),
    });
    talkRef.current = talk;
    const talkSupported = realtimeVoice && talk.supported;
    const { start: talkStart, end: talkEnd } = talk;
    const startTalk = useCallback(() => {
        sttAbort();
        stopSpeaking();
        setError(null);
        setTalkNotice(null);
        void talkStart();
    }, [sttAbort, talkStart]);
    // Live voice text grows between renders of `messages`; keep it in view.
    useEffect(() => {
        const el = messagesRef.current;
        if (!el || !stickToBottomRef.current)
            return;
        el.scrollTop = el.scrollHeight;
    }, [talk.liveText, talkNotice]);
    const startVoiceChat = useCallback(() => {
        if (voiceChatRef.current)
            return;
        sttAbort();
        stopSpeaking();
        setError(null);
        voiceChatRef.current = true;
        setVoiceChat(true);
        listenForVoiceChat();
    }, [sttAbort, listenForVoiceChat]);
    // Stop button: in Talk mode an interruption means "let me say something
    // else", so go straight back to listening.
    const onStopClick = useCallback(() => {
        stop();
        if (voiceChatRef.current)
            listenForVoiceChat();
    }, [stop, listenForVoiceChat]);
    // Speak every NEW assistant turn out loud when the speaker is on or Talk
    // mode is active. Track the last-spoken index so we don't re-speak the
    // whole history when conversations are loaded from the picker. When
    // speaking switches on, snap the watermark to the current end so we
    // don't backfill turns the user has already read. In Talk mode the mic
    // re-opens when the reply has been read.
    const speakReplies = ttsActive || voiceChat;
    const prevSpeakRepliesRef = useRef(false);
    useEffect(() => {
        // Consume the Stop flag on the commit that added the partial reply,
        // even when speaking is off, so it can't swallow a later reply.
        const skipThisReply = skipSpeakRef.current;
        skipSpeakRef.current = false;
        if (speakReplies && !prevSpeakRepliesRef.current) {
            lastSpokenRef.current = messages.length - 1;
        }
        prevSpeakRepliesRef.current = speakReplies;
        if (!speakReplies)
            return;
        const lastIdx = messages.length - 1;
        if (lastIdx < 0)
            return;
        const last = messages[lastIdx];
        if (!last || last.role !== 'assistant')
            return;
        if (lastIdx <= lastSpokenRef.current)
            return;
        lastSpokenRef.current = lastIdx;
        // A reply cut short with Stop is not read out.
        if (skipThisReply)
            return;
        if (voiceChatRef.current)
            setVoiceChatSpeaking(true);
        speak(markdownToPlainText(last.content), effectiveVoiceLang, () => {
            if (voiceChatRef.current)
                listenForVoiceChat();
        });
    }, [messages, speakReplies, effectiveVoiceLang, listenForVoiceChat]);
    // Silence in-flight TTS when speaking is turned off, the panel is
    // hidden (popup closed, or docked panel closed), or the component
    // unmounts. `stopSpeaking()` is a silent no-op when synthesis is
    // unsupported, so the unconditional cleanup is safe.
    const panelHidden = (isPopup && !open) || (isDocked && (dockedClosed || dockCollapsed));
    useEffect(() => {
        if (!speakReplies || panelHidden)
            stopSpeaking();
        return () => {
            stopSpeaking();
        };
    }, [panelHidden, speakReplies]);
    // Hiding the panel also ends Talk mode and any dictation.
    useEffect(() => {
        if (!panelHidden)
            return;
        endVoiceChat();
        talkRef.current?.end();
        sttAbort();
    }, [panelHidden, endVoiceChat, sttAbort]);
    const loadConversation = useCallback(async (id) => {
        // Spoken turns still held belong to the conversation being left.
        talkRef.current?.end();
        setTalkNotice(null);
        setHistoryLoading(true);
        setError(null);
        try {
            const result = await storage.get({ appId, id });
            if (!result.conversation) {
                setError(s.conversationNotFound);
                return;
            }
            // A reply still streaming for the previous conversation must not
            // land in (or re-point) the one being opened.
            abandonTurn();
            beginConversation(result.conversation.id);
            // Treat the loaded replies as already read.
            lastSpokenRef.current = result.conversation.messages.length - 1;
            setMessages(result.conversation.messages);
            setStoppedNotice(false);
            setView('chat');
            stickToBottomRef.current = true;
            requestFocus('force');
        }
        catch (err) {
            setError(formatString(s.genericError, { error: formatError(err) }));
        }
        finally {
            setHistoryLoading(false);
        }
    }, [appId, storage, s, requestFocus, abandonTurn, beginConversation]);
    // Opening the popup / docked panel is an explicit gesture: put the
    // cursor in the text box (skipped on touch screens, where it would pop
    // the on-screen keyboard over the conversation). Only on a hidden →
    // visible transition — a docked panel that is visible on page load
    // must not grab focus from the host.
    const prevPanelHiddenRef = useRef(panelHidden);
    useEffect(() => {
        const wasHidden = prevPanelHiddenRef.current;
        prevPanelHiddenRef.current = panelHidden;
        if (panelHidden || !wasHidden)
            return;
        if (!isPopup && !isDocked)
            return;
        if (typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches)
            return;
        requestFocus('force');
    }, [panelHidden, isPopup, isDocked, requestFocus]);
    // Popup mode opens/closes via the floating launcher; docked mode is
    // visible unless the user has collapsed it to the rail; inline mode
    // is always visible.
    const showPanel = isPopup ? open : isDocked ? !dockCollapsed && !dockedClosed : true;
    const rootClass = `ada-copilot-root ada-copilot-root-${layout}`;
    const panelClass = [
        'ada-copilot-panel',
        `ada-copilot-panel-${layout}`,
        popupFullscreen ? 'ada-copilot-panel-fullscreen' : '',
        popupGesture ? 'ada-copilot-panel-gesturing' : '',
    ]
        .filter(Boolean)
        .join(' ');
    // Inline width override for docked mode only. When the user has not
    // chosen a width yet (`dockWidthPx === null`) we let the stylesheet's
    // default win; otherwise we set it in px so resize feedback is
    // instant and no class-name churn is needed. A full-screen (phone)
    // popup ignores the stored floating rect.
    const panelStyle = isDocked && dockWidthPx !== null
        ? { ['--ada-copilot-dock-current-width']: `${dockWidthPx}px` }
        : isPopup && popupRect !== null && !popupFullscreen
            ? {
                // Switch from the CSS bottom-right anchor to explicit
                // positioning once the user has moved/resized the window.
                left: `${popupRect.left}px`,
                top: `${popupRect.top}px`,
                right: 'auto',
                bottom: 'auto',
                width: `${popupRect.width}px`,
                height: `${popupRect.height}px`,
            }
            : undefined;
    const popupGesturesOn = isPopup && !popupFullscreen;
    // The eight popup resize handles: four edges + four corners. `dir`
    // says which edges the gesture moves (the opposite edge stays fixed).
    const POPUP_RESIZE_HANDLES = [
        { key: 'n', dir: { x: 0, y: -1 } },
        { key: 's', dir: { x: 0, y: 1 } },
        { key: 'w', dir: { x: -1, y: 0 } },
        { key: 'e', dir: { x: 1, y: 0 } },
        { key: 'nw', dir: { x: -1, y: -1 } },
        { key: 'ne', dir: { x: 1, y: -1 } },
        { key: 'sw', dir: { x: -1, y: 1 } },
        { key: 'se', dir: { x: 1, y: 1 } },
    ];
    const canSend = draft.trim().length > 0 || attachments.length > 0;
    const placeholder = dictating || (voiceChat && stt.listening)
        ? s.inputPlaceholderDictating
        : canAttachFiles && attachments.length === 0
            ? s.inputPlaceholderWithFiles
            : s.inputPlaceholder;
    const liveAssistantText = streamingText || talk.liveText;
    const talkPhaseText = talk.phase === 'connecting'
        ? s.talkConnecting
        : talk.phase === 'thinking'
            ? s.voiceChatThinking
            : talk.phase === 'tool'
                ? (talk.toolLabel ?? s.toolWorking)
                : talk.phase === 'speaking'
                    ? s.talkSpeaking
                    : s.voiceChatListening;
    const voiceChatPhase = stt.listening
        ? 'listening'
        : pending
            ? 'thinking'
            : voiceChatSpeaking
                ? 'speaking'
                : 'listening';
    const voiceChatPhaseText = voiceChatPhase === 'thinking'
        ? s.voiceChatThinking
        : voiceChatPhase === 'speaking'
            ? s.voiceChatSpeaking
            : s.voiceChatListening;
    const headerClass = [
        'ada-copilot-header',
        header === 'compact' ? 'ada-copilot-header-compact' : '',
        popupGesturesOn ? 'ada-copilot-header-draggable' : '',
    ]
        .filter(Boolean)
        .join(' ');
    const subtitleText = subtitle ?? appId;
    return (_jsxs("div", { className: rootClass, part: "root", children: [isDocked && dockCollapsed && !dockedClosed ? (
            // The entire rail is the expand control (not just the chevron), so
            // a click anywhere on the strip reopens the panel — important when
            // a host's fixed header overlaps the top of the rail where the
            // chevron sits.
            _jsx("button", { type: "button", className: `ada-copilot-rail ada-copilot-rail-${dockSide}`, "aria-label": formatString(s.expand, { title }), title: formatString(s.expand, { title }), onClick: toggleDockCollapsed, part: "rail", children: dockSide === 'left' ? _jsx(ChevronRightIcon, {}) : _jsx(ChevronLeftIcon, {}) })) : null, showPanel ? (_jsxs("div", { ref: panelRef, className: panelClass, role: isPopup ? 'dialog' : 'region', "aria-label": title, "aria-modal": false, part: "panel", ...(panelStyle ? { style: panelStyle } : {}), children: [popupGesturesOn
                        ? POPUP_RESIZE_HANDLES.map(({ key, dir }) => (_jsx("div", { className: `ada-copilot-resize-edge ada-copilot-resize-edge-${key}`, role: "separator", "aria-label": s.resize, onPointerDown: (e) => onPopupResizePointerDown(e, dir), onPointerMove: onPopupGesturePointerMove, onPointerUp: onPopupGesturePointerUp, onPointerCancel: onPopupGesturePointerUp }, key)))
                        : null, isDocked ? (_jsx("div", { className: resizing
                            ? `ada-copilot-resize-handle ada-copilot-resize-handle-${dockSide} ada-copilot-resize-handle-active`
                            : `ada-copilot-resize-handle ada-copilot-resize-handle-${dockSide}`, role: "separator", "aria-orientation": "vertical", "aria-label": s.resize, onPointerDown: onResizePointerDown, onPointerMove: onResizePointerMove, onPointerUp: onResizePointerUp, onPointerCancel: onResizePointerUp })) : null, _jsxs("div", { className: headerClass, part: "header", ...(popupGesturesOn
                            ? {
                                onPointerDown: onPopupHeaderPointerDown,
                                onPointerMove: onPopupGesturePointerMove,
                                onPointerUp: onPopupGesturePointerUp,
                                onPointerCancel: onPopupGesturePointerUp,
                            }
                            : {}), children: [header === 'full' ? (_jsxs("div", { className: "ada-copilot-header-text", children: [_jsx("div", { className: "ada-copilot-title", part: "title", children: title }), subtitleText ? (_jsx("div", { className: "ada-copilot-subtitle", part: "subtitle", children: subtitleText })) : null] })) : null, _jsxs("div", { className: "ada-copilot-header-actions", part: "actions", children: [ttsSupported ? (_jsx("button", { type: "button", className: speakerEnabled
                                            ? 'ada-copilot-iconbtn ada-copilot-iconbtn-on'
                                            : 'ada-copilot-iconbtn', "aria-label": speakerEnabled ? s.speakerOn : s.speakerOff, "aria-pressed": speakerEnabled, title: speakerEnabled ? s.speakerOn : s.speakerOff, onClick: () => setSpeakerEnabled((v) => !v), children: speakerEnabled ? _jsx(SpeakerOnIcon, {}) : _jsx(SpeakerOffIcon, {}) })) : null, historyEnabled && view === 'chat' ? (_jsx("button", { type: "button", className: "ada-copilot-iconbtn", "aria-label": s.history, title: s.history, onClick: () => void openHistory(), children: _jsx(HistoryIcon, {}) })) : null, view === 'history' ? (_jsx("button", { type: "button", className: "ada-copilot-iconbtn", "aria-label": s.back, title: s.back, onClick: () => {
                                            setView('chat');
                                            requestFocus('force');
                                        }, children: _jsx(BackIcon, {}) })) : null, _jsx("button", { type: "button", className: "ada-copilot-iconbtn", "aria-label": s.newConversation, title: s.newConversation, onClick: startNew, children: _jsx(PlusIcon, {}) }), isPopup ? (_jsx("button", { type: "button", className: "ada-copilot-close", "aria-label": s.close, title: s.close, onClick: () => setOpen(false), children: _jsx(CloseIcon, {}) })) : null, isDocked ? (_jsx("button", { type: "button", className: "ada-copilot-iconbtn", "aria-label": s.collapse, title: s.collapse, onClick: toggleDockCollapsed, children: dockSide === 'left' ? _jsx(ChevronLeftIcon, {}) : _jsx(ChevronRightIcon, {}) })) : null, isDocked ? (_jsx("button", { type: "button", className: "ada-copilot-close", "aria-label": s.close, title: s.close, onClick: () => setDockedClosed(true), children: _jsx(CloseIcon, {}) })) : null] })] }), enableModelPicker && view === 'chat' ? (_jsx(ModelPickerRow, { models: models, currentKey: activeModelKey, onPick: onPickModel, disabled: pending, strings: s })) : null, view === 'history' ? (_jsxs("div", { className: "ada-copilot-messages", part: "messages", children: [historyLoading ? (_jsxs("div", { className: "ada-copilot-status", role: "status", children: [_jsx("span", { className: "ada-copilot-spinner", "aria-hidden": "true" }), s.historyLoading] })) : conversations.length === 0 ? (_jsx("div", { className: "ada-copilot-empty", children: s.historyEmpty })) : (conversations.map((c) => (_jsxs("button", { type: "button", className: "ada-copilot-history-row", onClick: () => void loadConversation(c.id), children: [_jsxs("div", { className: "ada-copilot-history-title", children: [c.pinned ? '📌 ' : '', c.title] }), _jsxs("div", { className: "ada-copilot-history-meta", children: [formatMessageCount(s, lang, c.messageCount), " \u00B7", ' ', formatRelativeTime(c.updatedAt, lang)] })] }, c.id)))), error ? (_jsx("div", { className: "ada-copilot-bubble ada-copilot-bubble-error", role: "alert", children: error })) : null] })) : (_jsxs("div", { className: "ada-copilot-messages", ref: messagesRef, onScroll: onMessagesScroll, role: "log", "aria-live": "polite", "aria-relevant": "additions", part: "messages", children: [messages.length === 0 && !pending && !error ? (_jsxs("div", { className: "ada-copilot-empty", part: "empty", children: [_jsx("span", { className: "ada-copilot-empty-icon", "aria-hidden": "true", children: _jsx(ChatIcon, {}) }), _jsx("div", { children: s.emptyState }), suggestions && suggestions.length > 0 ? (_jsxs("div", { className: "ada-copilot-suggestions", part: "suggestions", children: [_jsx("div", { className: "ada-copilot-suggestions-label", children: s.suggestionsLabel }), suggestions.map((q, i) => (_jsx("button", { type: "button", className: "ada-copilot-suggestion", part: "suggestion", onClick: () => void submit(q), children: q }, `${i}-${q}`)))] })) : null] })) : null, messages.map((m, i) => (_jsxs("div", { className: m.role === 'user'
                                    ? 'ada-copilot-bubble ada-copilot-bubble-user'
                                    : 'ada-copilot-bubble ada-copilot-bubble-assistant', part: m.role === 'user' ? 'bubble bubble-user' : 'bubble bubble-assistant', children: [m.role === 'assistant' ? (
                                    // Assistant turns are markdown — render to sanitised HTML.
                                    // User turns stay raw text (no surprise formatting of what
                                    // the user literally typed).
                                    _jsx("div", { className: "ada-copilot-md", dangerouslySetInnerHTML: { __html: renderMarkdown(m.content) } })) : (m.content), m.attachments && m.attachments.length > 0 ? (_jsx("div", { className: "ada-copilot-bubble-attachments", children: m.attachments.map((a, j) => (_jsxs("span", { className: "ada-copilot-chip ada-copilot-chip-static", title: a.name, children: [_jsx(FileIcon, {}), _jsx("span", { className: "ada-copilot-chip-name", children: a.name })] }, `${a.name}-${j}`))) })) : null] }, i))), liveAssistantText ? (
                            // Hidden from the live region while it grows; the committed
                            // message is announced once when the turn completes. In a
                            // realtime voice session this is the reply being spoken.
                            _jsxs("div", { className: "ada-copilot-bubble ada-copilot-bubble-assistant", part: "bubble bubble-assistant", "aria-hidden": "true", children: [_jsx("div", { className: "ada-copilot-md", dangerouslySetInnerHTML: { __html: renderMarkdown(liveAssistantText) } }), _jsx("span", { className: "ada-copilot-cursor", "aria-hidden": "true", children: "\u258A" })] })) : null, pending && toolStatus ? (_jsxs("div", { className: "ada-copilot-status", part: "status", role: "status", children: [_jsx("span", { className: "ada-copilot-spinner", "aria-hidden": "true" }), toolStatus] })) : null, pending && !streamingText && !toolStatus ? (_jsxs("div", { className: "ada-copilot-typing", part: "typing", role: "status", children: [_jsx("span", { className: "ada-copilot-typing-dot", "aria-hidden": "true" }), _jsx("span", { className: "ada-copilot-typing-dot", "aria-hidden": "true" }), _jsx("span", { className: "ada-copilot-typing-dot", "aria-hidden": "true" }), _jsx("span", { className: "ada-copilot-sr-only", children: s.thinking })] })) : null, stoppedNotice ? (_jsx("div", { className: "ada-copilot-notice", part: "notice", children: s.stopped })) : null, talkNotice ? (_jsx("div", { className: "ada-copilot-notice", part: "notice", children: talkNotice })) : null, error ? (_jsx("div", { className: "ada-copilot-bubble ada-copilot-bubble-error", part: "bubble bubble-error", role: "alert", children: error })) : null] })), view === 'chat' ? (_jsxs("form", { className: dragging
                            ? 'ada-copilot-form ada-copilot-form-dragover'
                            : 'ada-copilot-form', part: "composer", onSubmit: onSubmit, onDragOver: onDragOver, onDragLeave: onDragLeave, onDrop: onDrop, children: [talk.active ? (_jsxs("div", { className: talk.phase === 'listening'
                                    ? 'ada-copilot-voicebar ada-copilot-voicebar-listening'
                                    : 'ada-copilot-voicebar', part: "voice-bar", role: "status", children: [_jsx("span", { className: "ada-copilot-voicebar-dot", "aria-hidden": "true" }), _jsx("span", { className: "ada-copilot-voicebar-text", children: talk.phase === 'speaking' ? (
                                        // Two buttons share the bar while the reply plays;
                                        // the phase alone keeps it readable in narrow panels.
                                        talkPhaseText) : (_jsxs(_Fragment, { children: [_jsx("strong", { children: s.voiceChatActive }), " \u00B7 ", talkPhaseText] })) }), talk.phase === 'speaking' ? (_jsx("button", { type: "button", className: "ada-copilot-voicebar-interrupt", part: "voice-interrupt", title: s.talkInterruptTitle, onClick: talk.interrupt, children: s.talkInterrupt })) : null, _jsx("button", { type: "button", className: "ada-copilot-voicebar-end", onClick: talkEnd, children: s.voiceChatEnd })] })) : null, voiceChat ? (_jsxs("div", { className: voiceChatPhase === 'listening'
                                    ? 'ada-copilot-voicebar ada-copilot-voicebar-listening'
                                    : 'ada-copilot-voicebar', part: "voice-bar", role: "status", children: [_jsx("span", { className: "ada-copilot-voicebar-dot", "aria-hidden": "true" }), _jsxs("span", { className: "ada-copilot-voicebar-text", children: [_jsx("strong", { children: s.voiceChatActive }), " \u00B7 ", voiceChatPhaseText] }), _jsx("button", { type: "button", className: "ada-copilot-voicebar-end", onClick: endVoiceChat, children: s.voiceChatEnd })] })) : null, attachments.length > 0 ? (_jsx("div", { className: "ada-copilot-attachments", "aria-label": s.attachedFiles, children: attachments.map((a, i) => (_jsxs("span", { className: "ada-copilot-chip", children: [_jsx(FileIcon, {}), _jsx("span", { className: "ada-copilot-chip-name", title: a.name, children: a.name }), a.sizeBytes !== undefined ? (_jsx("span", { className: "ada-copilot-chip-size", children: formatBytes(a.sizeBytes) })) : null, _jsx("button", { type: "button", className: "ada-copilot-chip-remove", "aria-label": formatString(s.removeFile, { name: a.name }), onClick: () => removeAttachment(i), disabled: pending, children: "\u00D7" })] }, `${a.name}-${i}`))) })) : null, _jsxs("div", { className: "ada-copilot-inputbox", children: [_jsx("textarea", { ref: inputRef, className: "ada-copilot-input", part: "input", "aria-label": s.inputAriaLabel, placeholder: placeholder, value: draft, onChange: (e) => setDraft(e.target.value), onKeyDown: onKeyDown, rows: 1 }), _jsxs("div", { className: "ada-copilot-toolbar", children: [_jsxs("div", { className: "ada-copilot-toolbar-start", children: [canAttachFiles ? (_jsx("button", { type: "button", className: "ada-copilot-tool", part: "attach", "aria-label": s.attachFile, title: s.attachFile, onClick: () => fileInputRef.current?.click(), disabled: pending || talk.active, children: _jsx(ClipIcon, {}) })) : null, sttSupported ? (_jsxs("button", { type: "button", className: dictating ? 'ada-copilot-tool ada-copilot-tool-on' : 'ada-copilot-tool', part: "dictate", "aria-pressed": dictating, "aria-label": dictating ? s.dictateStop : s.dictateTooltip, title: dictating ? s.dictateStop : s.dictateTooltip, onClick: toggleDictation, disabled: voiceChat || talk.active, children: [_jsx(MicIcon, {}), _jsx("span", { className: "ada-copilot-tool-label", children: s.dictate })] })) : null, voiceChatSupported ? (_jsxs("button", { type: "button", className: voiceChat ? 'ada-copilot-tool ada-copilot-tool-active' : 'ada-copilot-tool', part: "voice-chat", "aria-pressed": voiceChat, "aria-label": voiceChat ? s.voiceChatEnd : s.voiceChatTooltip, title: voiceChat ? s.voiceChatEnd : s.voiceChatTooltip, onClick: voiceChat ? endVoiceChat : startVoiceChat, disabled: !voiceChat && (pending || dictating), children: [_jsx(HeadsetIcon, {}), _jsx("span", { className: "ada-copilot-tool-label", children: s.voiceChat })] })) : null, talkSupported ? (_jsxs("button", { type: "button", className: talk.active ? 'ada-copilot-tool ada-copilot-tool-active' : 'ada-copilot-tool', part: "voice-chat", "aria-pressed": talk.active, "aria-label": talk.active ? s.voiceChatEnd : s.talkTooltip, title: talk.active ? s.voiceChatEnd : s.talkTooltip, onClick: talk.active ? talkEnd : startTalk, disabled: !talk.active && (pending || dictating), children: [_jsx(HeadsetIcon, {}), _jsx("span", { className: "ada-copilot-tool-label", children: s.voiceChat })] })) : null] }), pending ? (_jsxs("button", { type: "button", className: "ada-copilot-send ada-copilot-stop", part: "stop", "aria-label": s.stopAria, title: s.stopAria, onClick: (e) => {
                                                    // The second click of a double-clicked Send lands
                                                    // here once the button has swapped — ignore it.
                                                    if (e.detail > 1)
                                                        return;
                                                    onStopClick();
                                                }, children: [_jsx(StopIcon, {}), _jsx("span", { children: s.stop })] })) : (_jsxs("button", { type: "submit", className: "ada-copilot-send", part: "send", disabled: !canSend, children: [_jsx(SendIcon, {}), _jsx("span", { children: s.send })] }))] })] }), _jsx("div", { className: "ada-copilot-hint", part: "hint", "aria-hidden": "true", children: s.enterHint }), canAttachFiles ? (_jsx("input", { ref: fileInputRef, type: "file", multiple: true, accept: fileUploadAccept ? fileUploadAccept : undefined, className: "ada-copilot-file-input", onChange: onFileInputChange })) : null] })) : null] })) : null, (isPopup && !open) || (isDocked && dockedClosed) ? (
            // Pure opener now: the popup is closed from its own header ✕, so the
            // launcher only shows when the panel is hidden and always opens it.
            _jsx("button", { type: "button", className: "ada-copilot-launcher", part: "launcher", "aria-label": formatString(s.open, { title }), title: formatString(s.open, { title }), onClick: () => (isPopup ? setOpen(true) : setDockedClosed(false)), children: _jsx(ChatIcon, {}) })) : null] }));
}
function ChatIcon() {
    return (_jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: _jsx("path", { d: "M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" }) }));
}
function CloseIcon() {
    return (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", width: "1.25rem", height: "1.25rem", "aria-hidden": "true", children: [_jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), _jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }));
}
function HistoryIcon() {
    return (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", width: "1.1rem", height: "1.1rem", "aria-hidden": "true", children: [_jsx("path", { d: "M3 12a9 9 0 1 0 3-6.7" }), _jsx("polyline", { points: "3 4 3 9 8 9" }), _jsx("line", { x1: "12", y1: "7", x2: "12", y2: "12" }), _jsx("line", { x1: "12", y1: "12", x2: "15", y2: "14" })] }));
}
function PlusIcon() {
    return (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", width: "1.1rem", height: "1.1rem", "aria-hidden": "true", children: [_jsx("line", { x1: "12", y1: "5", x2: "12", y2: "19" }), _jsx("line", { x1: "5", y1: "12", x2: "19", y2: "12" })] }));
}
function MicIcon() {
    return (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", width: "1.1rem", height: "1.1rem", "aria-hidden": "true", children: [_jsx("path", { d: "M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" }), _jsx("path", { d: "M19 10v2a7 7 0 0 1-14 0v-2" }), _jsx("line", { x1: "12", y1: "19", x2: "12", y2: "23" }), _jsx("line", { x1: "8", y1: "23", x2: "16", y2: "23" })] }));
}
function SpeakerOnIcon() {
    return (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", width: "1.1rem", height: "1.1rem", "aria-hidden": "true", children: [_jsx("polygon", { points: "11 5 6 9 2 9 2 15 6 15 11 19 11 5" }), _jsx("path", { d: "M15.54 8.46a5 5 0 0 1 0 7.07" }), _jsx("path", { d: "M19.07 4.93a10 10 0 0 1 0 14.14" })] }));
}
function SpeakerOffIcon() {
    return (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", width: "1.1rem", height: "1.1rem", "aria-hidden": "true", children: [_jsx("polygon", { points: "11 5 6 9 2 9 2 15 6 15 11 19 11 5" }), _jsx("line", { x1: "22", y1: "9", x2: "16", y2: "15" }), _jsx("line", { x1: "16", y1: "9", x2: "22", y2: "15" })] }));
}
function ClipIcon() {
    return (_jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", width: "1.1rem", height: "1.1rem", "aria-hidden": "true", children: _jsx("path", { d: "M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" }) }));
}
function FileIcon() {
    return (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", width: "0.9rem", height: "0.9rem", "aria-hidden": "true", children: [_jsx("path", { d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" }), _jsx("polyline", { points: "14 2 14 8 20 8" })] }));
}
function ChevronDownIcon() {
    return (_jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", width: "0.9rem", height: "0.9rem", "aria-hidden": "true", children: _jsx("polyline", { points: "6 9 12 15 18 9" }) }));
}
function ChevronLeftIcon() {
    return (_jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", width: "1.1rem", height: "1.1rem", "aria-hidden": "true", children: _jsx("polyline", { points: "15 18 9 12 15 6" }) }));
}
function ChevronRightIcon() {
    return (_jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", width: "1.1rem", height: "1.1rem", "aria-hidden": "true", children: _jsx("polyline", { points: "9 18 15 12 9 6" }) }));
}
function SendIcon() {
    return (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2.25", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("line", { x1: "12", y1: "19", x2: "12", y2: "5" }), _jsx("polyline", { points: "5 12 12 5 19 12" })] }));
}
function StopIcon() {
    return (_jsx("svg", { viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", children: _jsx("rect", { x: "6", y: "6", width: "12", height: "12", rx: "2" }) }));
}
function HeadsetIcon() {
    return (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", children: [_jsx("path", { d: "M3 18v-6a9 9 0 0 1 18 0v6" }), _jsx("path", { d: "M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z" }), _jsx("path", { d: "M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" })] }));
}
/**
 * Type-as-you-filter combobox surfaced when `enableModelPicker` is on.
 * Renders a single row showing the current model key; clicking it opens
 * a popover with a search box and a filtered scroll list. Selecting a
 * row commits the choice and collapses the popover. The model catalog
 * comes from the parent — this component is purely presentational and
 * keeps its own filter state.
 *
 * Filter logic: case-insensitive substring match against the catalog
 * key, the resolved model id (for direct entries), and the entry's
 * description if any. That covers the realistic search patterns
 * ("sonnet", "openai", "anthropic-text-mid").
 */
function ModelPickerRow({ models, currentKey, onPick, disabled, strings, }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const containerRef = useRef(null);
    useEffect(() => {
        if (!open)
            return;
        // Collapse the popover when the user clicks outside it. Use
        // capture phase so the listener fires before a click inside
        // another widget control can swallow it.
        const onDocPointerDown = (e) => {
            const node = containerRef.current;
            if (!node)
                return;
            if (e.target instanceof Node && node.contains(e.target))
                return;
            setOpen(false);
        };
        document.addEventListener('pointerdown', onDocPointerDown, true);
        return () => document.removeEventListener('pointerdown', onDocPointerDown, true);
    }, [open]);
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (q.length === 0)
            return models;
        return models.filter((m) => {
            const e = m.entry;
            const haystack = [m.key];
            if (typeof e === 'string') {
                haystack.push(e);
            }
            else if ('mapsTo' in e) {
                haystack.push(e.mapsTo);
                if (e.description)
                    haystack.push(e.description);
            }
            else {
                haystack.push(e.model);
                if (e.description)
                    haystack.push(e.description);
                haystack.push(e.vendor);
            }
            return haystack.some((s) => s.toLowerCase().includes(q));
        });
    }, [models, query]);
    return (_jsxs("div", { className: "ada-copilot-modelpicker", ref: containerRef, children: [_jsxs("button", { type: "button", className: "ada-copilot-modelpicker-trigger", "aria-haspopup": "listbox", "aria-expanded": open, disabled: disabled, onClick: () => setOpen((v) => !v), children: [_jsx("span", { className: "ada-copilot-modelpicker-label", children: strings.modelLabel }), _jsx("span", { className: "ada-copilot-modelpicker-value", children: currentKey }), _jsx(ChevronDownIcon, {})] }), open ? (_jsxs("div", { className: "ada-copilot-modelpicker-panel", role: "listbox", children: [_jsx("input", { type: "search", className: "ada-copilot-modelpicker-search", placeholder: strings.modelFilter, autoFocus: true, value: query, onChange: (e) => setQuery(e.target.value), onKeyDown: (e) => {
                            if (e.key === 'Escape') {
                                setOpen(false);
                            }
                            else if (e.key === 'Enter' && filtered.length > 0) {
                                e.preventDefault();
                                const first = filtered[0];
                                if (first) {
                                    onPick(first.key);
                                    setOpen(false);
                                    setQuery('');
                                }
                            }
                        } }), _jsx("div", { className: "ada-copilot-modelpicker-list", children: models.length === 0 ? (_jsx("div", { className: "ada-copilot-modelpicker-empty", children: strings.modelListUnavailable })) : filtered.length === 0 ? (_jsx("div", { className: "ada-copilot-modelpicker-empty", children: formatString(strings.modelNoMatch, { query }) })) : (filtered.map((m) => {
                            const e = m.entry;
                            const secondary = typeof e === 'string'
                                ? `alias → ${e}`
                                : 'mapsTo' in e
                                    ? `alias → ${e.mapsTo}${e.description ? ` · ${e.description}` : ''}`
                                    : `${e.vendor} · ${e.outputType}${e.description ? ` · ${e.description}` : ''}`;
                            const isCurrent = m.key === currentKey;
                            return (_jsxs("button", { type: "button", role: "option", "aria-selected": isCurrent, className: isCurrent
                                    ? 'ada-copilot-modelpicker-row ada-copilot-modelpicker-row-current'
                                    : 'ada-copilot-modelpicker-row', onClick: () => {
                                    onPick(m.key);
                                    setOpen(false);
                                    setQuery('');
                                }, children: [_jsx("span", { className: "ada-copilot-modelpicker-row-key", children: m.key }), _jsx("span", { className: "ada-copilot-modelpicker-row-meta", children: secondary })] }, m.key));
                        })) })] })) : null] }));
}
function BackIcon() {
    return (_jsxs("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", width: "1.1rem", height: "1.1rem", "aria-hidden": "true", children: [_jsx("line", { x1: "19", y1: "12", x2: "5", y2: "12" }), _jsx("polyline", { points: "12 19 5 12 12 5" })] }));
}
// Stored shape `{ speaker }` under `ada-copilot-voice:<appId>`. Older
// entries also carried a `mic` flag (the removed header mic toggle);
// it is ignored.
function readSpeakerPref(key) {
    if (typeof window === 'undefined')
        return false;
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw)
            return false;
        const parsed = JSON.parse(raw);
        return parsed.speaker === true;
    }
    catch {
        return false;
    }
}
function writeSpeakerPref(key, speaker) {
    if (typeof window === 'undefined')
        return;
    try {
        window.localStorage.setItem(key, JSON.stringify({ speaker }));
    }
    catch {
        // Storage quota exceeded or disabled by user policy — silently
        // ignore. The toggle still works for the lifetime of the page.
    }
}
/** Append dictated text to whatever the user had already typed. */
function joinDictation(base, spoken) {
    const head = base.replace(/\s+$/, '');
    return head ? `${head} ${spoken}` : spoken;
}
function formatError(err) {
    if (err instanceof Error)
        return err.message;
    if (typeof err === 'string')
        return err;
    return 'Unknown error';
}
function deriveTitle(history, fallback = 'New conversation') {
    const firstUser = history.find((m) => m.role === 'user');
    if (!firstUser)
        return fallback;
    const text = firstUser.content.replace(/\s+/g, ' ').trim();
    if (text.length <= 50)
        return text;
    return text.slice(0, 47) + '…';
}
/**
 * Default per-file size cap (10 MB). Files larger than this are
 * rejected client-side with an inline error. Keep this conservative
 * until a real upload path lands — every byte of an attachment rides
 * inline as base64 in every JSON-RPC envelope this turn (and every
 * subsequent turn that re-sends the user message in `history`).
 */
const DEFAULT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
/**
 * Fallback alias used for the capability gate when the host doesn't
 * pass an explicit `model` prop. Kept in sync with
 * `ConversationDriver.DEFAULT_MODEL` — divergence would mean the
 * widget hides the clip button on a model that the driver then
 * happily sends a file to (or vice versa).
 */
const DEFAULT_MODEL_KEY = 'anthropic-text-mid';
/**
 * Follow alias chains in the cached catalog until a concrete entry
 * (with `capabilities`) is reached, or until we cycle / fall off the
 * end. Returns `undefined` for unknown keys, alias cycles, or empty
 * catalogs — every call site falls back to "assume not supported"
 * in that case.
 */
function resolveCapabilities(models, key) {
    const visited = new Set();
    let cursor = key;
    while (!visited.has(cursor)) {
        visited.add(cursor);
        const found = models.find((m) => m.key === cursor);
        if (!found)
            return undefined;
        const e = found.entry;
        if (typeof e === 'string') {
            cursor = e;
            continue;
        }
        if ('mapsTo' in e) {
            cursor = e.mapsTo;
            continue;
        }
        return e.capabilities;
    }
    return undefined;
}
/**
 * Strip the `attachments` field from every message and prepend a
 * one-line `[file: <name>]` placeholder per attachment to the message
 * content. The result is what gets persisted: base64 bytes never reach
 * `localStorage` or the server-side conversation store, but reloaded
 * conversations still surface which files were attached on each turn.
 */
function stripAttachmentsForStorage(history) {
    return history.map((m) => {
        if (!m.attachments || m.attachments.length === 0) {
            // Drop the (absent) attachments key entirely; the wire shape
            // never carries it.
            const { attachments: _omit, ...rest } = m;
            return rest;
        }
        const placeholder = m.attachments
            .map((a) => `[file: ${a.name}]`)
            .join('\n');
        const content = m.content
            ? `${placeholder}\n${m.content}`
            : placeholder;
        const { attachments: _drop, ...rest } = m;
        return { ...rest, content };
    });
}
/**
 * Human-readable byte size for chips and error messages. Uses binary
 * units (KiB-style) but the unit labels stay short ("KB", "MB") to
 * match the conventions users see in the rest of the UI. Below 1 KB
 * we report the raw byte count — useful when a user accidentally drops
 * a 12-byte text file.
 */
function formatBytes(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
//# sourceMappingURL=Copilot.js.map